import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Headphones, Play, Pause, Volume2, VolumeX, X, Loader, Zap, ThumbsDown, ThumbsUp, Mic, MessageSquare, Send } from 'lucide-react';
import { SpeechRecognition } from "@capacitor-community/speech-recognition";
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../../../firebase/firebase'
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { SecureAPI } from '../config.js';
import MeditationReminderService from './MeditationReminderService';

// Ambient music files
const AMBIENT_TRACKS = [
  '/music/ambience10.mp3',
  '/music/ambience7.mp3',
  '/music/ambience8.mp3',
  '/music/ambience6.mp3',
  '/music/ambience5.mp3',
  '/music/ambience9.mp3',
];

// Background images organized by time of day
const REFLECTION_BACKGROUNDS = {
  morning: [
    '/image/session-img31.png',
    '/image/session-img32.png',
    '/image/session-img33.png',
    '/image/session-img34.png',
    '/image/session-img53.png'
  ],
  afternoon: [
    '/image/session-img36.png',
    '/image/session-img54.png',
    '/image/session-img47.png',
    '/image/session-img39.png'
  ],
  evening: [
    '/image/session-img40.png',
    '/image/session-img41.png',
    '/image/session-img42.png',
    '/image/session-img43.png',
    '/image/session-img44.png'
  ]
};

// Calm, therapeutic voices
const VOICE_CONFIG = {
  guide: '33045fd9-8010-43f6-b6b0-da3fbf326c29',
  therapist: '41745808-6790-4be4-83db-363e20db7e71'
};

// ============================================
// ✅ UPDATED: Helper function to call secure TTS
// ============================================
const callSecureTTS = async (text, voiceKey) => {
  try {
    const voiceId = VOICE_CONFIG[voiceKey];
    if (!voiceId) {
      throw new Error(`Unknown voice: ${voiceKey}`);
    }
    
    console.log(`🎙️ Calling secure TTS for ${voiceKey}:`, text.substring(0, 50));
    
    // Call your secure Cloud Function instead of Hume directly
    const audioBlob = await SecureAPI.callHumeTTS(text, voiceId);
    
    console.log('✅ Secure TTS response received');
    return audioBlob;
    
  } catch (error) {
    console.error('❌ Secure TTS Error:', error);
    throw error;
  }
};

const JournalReflectionAudio = ({ entries, userName, onClose,isEntryLocked }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentStatus, setCurrentStatus] = useState('');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('');
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [transcript, setTranscript] = useState([]);
  const [debugLog, setDebugLog] = useState([]);
const azureRecognizerRef = useRef(null);
const isMicActiveRef = useRef(false);
const accumulatedTranscriptRef = useRef('');
const silenceTimerRef = useRef(null);
const [firestoreUserName, setFirestoreUserName] = useState(null);
const [isLoadingUserName, setIsLoadingUserName] = useState(true);
const displayName = firestoreUserName || userName || 'there';
const [firestoreService, setFirestoreService] = useState(null);
  const audioQueueRef = useRef(new Map());
  const isPreloadingRef = useRef(new Set());
  const abortControllerRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const currentAudioRef = useRef(null);
  const isMutedRef = useRef(isMuted);
  const playbackSpeedRef = useRef(playbackSpeed);
  const recognitionRef = useRef(null);
const conversationHistoryRef = useRef([]);
const micAbortControllerRef = useRef(null);
const [isInitializing, setIsInitializing] = useState(false);
const [isMicActive, setIsMicActive] = useState(false);
const [userTranscript, setUserTranscript] = useState('');
const [hasShownGreeting, setHasShownGreeting] = useState(false);
const [isProcessingQuestion, setIsProcessingQuestion] = useState(false);
const [isListening, setIsListening] = useState(false);
const [reflectionEndTime, setReflectionEndTime] = useState(null);
const reflectionTimerRef = useRef(null);
const [loadingMessage, setLoadingMessage] = useState('');
const [isPaused, setIsPaused] = useState(false);
const [micStatus, setMicStatus] = useState(''); // 'listening', 'thinking', or ''
const lastUserActivityRef = useRef(Date.now());
const inactivityTimerRef = useRef(null);
const isHostSpeakingRef = useRef(false);
const lastReflectionTopicRef = useRef(''); // Track what hosts were discussing
const userQuestionTopicRef = useRef(''); // Track what user asked about
const [selectedSuggestion, setSelectedSuggestion] = useState(null);
const [showSuggestionConfirm, setShowSuggestionConfirm] = useState(false);
const [suggestionTopics, setSuggestionTopics] = useState([]);
const [isShowingFarewell, setIsShowingFarewell] = useState(false);
const [isSuggestionMode, setIsSuggestionMode] = useState(false);
const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);
const [lastVisitTime, setLastVisitTime] = useState(null);
const [timeSinceLastVisit, setTimeSinceLastVisit] = useState('');

const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
const [storageService, setStorageService] = useState(null);
// ✅ Auto-scroll transcript to bottom when new content arrives
const transcriptRef = useRef(null);
const [interactionMode, setInteractionMode] = useState(null); // null, 'voice', or 'text'
const [textInput, setTextInput] = useState('');
const [isSendingText, setIsSendingText] = useState(false);
const [showJoinModal, setShowJoinModal] = useState(false); // NEW: Modal for join confirmation
const [showIntentionModal, setShowIntentionModal] = useState(false);
const [selectedIntention, setSelectedIntention] = useState(null);
const [showAllSet, setShowAllSet] = useState(false);
const [intentionScrollIndex, setIntentionScrollIndex] = useState(0); // ✅ NEW
const [currentEntry, setCurrentEntry] = useState(null);
const [ambientTimeRemaining, setAmbientTimeRemaining] = useState(0);
const shouldWrapUpRef = useRef(false);

const shouldTriggerWrapUpRef = useRef(false);
const [hasScheduledNotifications, setHasScheduledNotifications] = useState(false);

// Check if already scheduled:
useEffect(() => {
  const checkNotificationStatus = async () => {
    try {
      if (!firestoreService?.isReady?.()) return;
      
      const userData = await firestoreService.getUserData();
      const hasScheduled = userData?.meditationNotificationsScheduled || false;
      setHasScheduledNotifications(hasScheduled);
      
      if (hasScheduled) {
        console.log('✅ User already has meditation notifications scheduled');
      }
    } catch (error) {
      console.log('Could not check notification status');
    }
  };
  
  checkNotificationStatus();
}, [firestoreService]);

// Function to schedule (one-time only):
const scheduleFirstTimeMeditationNotifications = async () => {
  if (hasScheduledNotifications) {
    console.log('✅ Meditation notifications already scheduled - skipping');
    return;
  }
  
  try {
    const userName = firestoreUserName || displayName || 'there';
    
    console.log(`🔔 Scheduling meditation notifications for ${userName}...`);
    
    const initialized = await MeditationReminderService.initialize();
    
    if (!initialized) {
      console.log('⚠️ Notification permissions not granted');
      return;
    }
    
    const scheduled = await MeditationReminderService.scheduleDailyReminders(userName);
    
    if (scheduled) {
      // Save to Firestore
      if (firestoreService?.isReady?.()) {
        await firestoreService.updateUserData({
          meditationNotificationsScheduled: true,
          firstMeditationDate: new Date().toISOString(),
          meditationNotificationUserName: userName
        });
        
        console.log('💾 Saved notification status to Firestore');
      }
      
      setHasScheduledNotifications(true);
      console.log('✅ Meditation notifications activated for the first time!');
      console.log(`📱 ${userName} will receive 3 daily reminders:`);
      console.log('   🌅 8:00 AM - Morning meditation');
      console.log('   🌤️ 2:00 PM - Afternoon reset');
      console.log('   🌙 8:00 PM - Evening wind down');
    }
  } catch (error) {
    console.error('❌ Failed to schedule meditation notifications:', error);
  }
};

useEffect(() => {
  if (!ambientAudioRef.current || !isPlaying) return;
  
  const interval = setInterval(() => {
    const audio = ambientAudioRef.current;
    if (audio && !audio.paused && !audio.ended) {
      const remaining = audio.duration - audio.currentTime;
      const elapsed = audio.currentTime;
      
      setAmbientTimeRemaining(remaining);
      
      // ✅ SMART TRIGGER: 45 seconds before end, but only if:
      // 1. Not already triggered
      // 2. No current audio playing
      // 3. User not in conversation mode
      // 4. Session has been running for at least 2 minutes
      
      const isCurrentAudioPlaying = currentAudioRef.current && !currentAudioRef.current.paused;
      const isUserChatting = interactionMode !== null;
      const hasRunLongEnough = elapsed > 120; // At least 2 min
      
      if (remaining <= 45 && 
          remaining > 0 && 
          !shouldTriggerWrapUpRef.current && 
          !isCurrentAudioPlaying && 
          !isUserChatting &&
          hasRunLongEnough) {
        
        shouldTriggerWrapUpRef.current = true;
        shouldWrapUpRef.current = true;
        
        log(`⏰ SMART WRAP-UP TRIGGER: 45s left in ${Math.floor(audio.duration / 60)}:${String(Math.round(audio.duration % 60)).padStart(2, '0')} track`, 'timer');
      }
    }
  }, 1000);
  
  return () => clearInterval(interval);
}, [isPlaying, interactionMode]);
const OPENING_TECHNIQUES = [
  {
    technique: 'grounding',
    description: 'Start with grounding in the present moment - feet on floor, hands in lap',
    cues: 'Notice where your body touches the ground. Feel the weight of your body. Anchor yourself here.'
  },
  {
    technique: 'breath_awareness',
    description: 'Begin by simply noticing the natural rhythm of breathing',
    cues: 'Find your breath without changing it. Just observe. Where do you feel it? Nose, throat, belly?'
  },
  {
    technique: 'body_scan_light',
    description: 'Quick mental scan from head to toe with gentle awareness',
    cues: 'Start at the crown of your head. Slowly travel down. Notice without judgment.'
  },
  {
    technique: 'comfort_space',
    description: 'Settle into a comfortable position - somewhere safe, supported',
    cues: 'Find where you feel most comfortable right now. Let your body be held by the space around you.'
  },
  {
    technique: 'sensory',
    description: 'Ground through 5 senses - what you feel, hear, sense around you',
    cues: 'What do you hear right now? What textures surround you? What can you smell?'
  },
  {
    technique: 'visualization_light',
    description: 'Imagine a place of calm - natural setting, sanctuary, safe space',
    cues: 'Picture a place where you feel at ease. See the colors, light, feel the temperature.'
  },
  {
    technique: 'heartbeat',
    description: 'Center on heartbeat and internal rhythm',
    cues: 'Place hand on heart. Feel the steady rhythm. Your body knows what to do.'
  },
  {
    technique: 'intention_setting',
    description: 'Begin by naming what you need - out loud or silently',
    cues: 'You chose to work on something meaningful. Why did that call to you right now? Sit with that.'
  }
];
const intentionOptions = [
  // Morning Focus
  { 
    id: 'morning-start', 
    label: 'Start Your Day Right', 
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="5"/>
        <line x1="12" y1="1" x2="12" y2="3"/>
        <line x1="12" y1="21" x2="12" y2="23"/>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
        <line x1="1" y1="12" x2="3" y2="12"/>
        <line x1="21" y1="12" x2="23" y2="12"/>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
      </svg>
    ),
    timeRelevant: 'morning'
  },
  { 
    id: 'energy', 
    label: 'Boost Energy & Motivation', 
    icon: (
<svg width="22" height="22" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.85357 3.85355L7.65355 3.05353C8.2981 2.40901 9.42858 1.96172 10.552 1.80125C11.1056 1.72217 11.6291 1.71725 12.0564 1.78124C12.4987 1.84748 12.7698 1.97696 12.8965 2.10357C13.0231 2.23018 13.1526 2.50125 13.2188 2.94357C13.2828 3.37086 13.2779 3.89439 13.1988 4.44801C13.0383 5.57139 12.591 6.70188 11.9464 7.34645L7.49999 11.7929L6.35354 10.6465C6.15827 10.4512 5.84169 10.4512 5.64643 10.6465C5.45117 10.8417 5.45117 11.1583 5.64643 11.3536L7.14644 12.8536C7.34171 13.0488 7.65829 13.0488 7.85355 12.8536L8.40073 12.3064L9.57124 14.2572C9.65046 14.3893 9.78608 14.4774 9.9389 14.4963C10.0917 14.5151 10.2447 14.4624 10.3535 14.3536L12.3535 12.3536C12.4648 12.2423 12.5172 12.0851 12.495 11.9293L12.0303 8.67679L12.6536 8.05355C13.509 7.19808 14.0117 5.82855 14.1887 4.58943C14.2784 3.9618 14.2891 3.33847 14.2078 2.79546C14.1287 2.26748 13.9519 1.74482 13.6035 1.39645C13.2552 1.04809 12.7325 0.871332 12.2045 0.792264C11.6615 0.710945 11.0382 0.721644 10.4105 0.8113C9.17143 0.988306 7.80189 1.491 6.94644 2.34642L6.32322 2.96968L3.07071 2.50504C2.91492 2.48278 2.75773 2.53517 2.64645 2.64646L0.646451 4.64645C0.537579 4.75533 0.484938 4.90829 0.50375 5.0611C0.522563 5.21391 0.61073 5.34954 0.742757 5.42876L2.69364 6.59928L2.14646 7.14645C2.0527 7.24022 2.00002 7.3674 2.00002 7.50001C2.00002 7.63261 2.0527 7.75979 2.14646 7.85356L3.64647 9.35356C3.84173 9.54883 4.15831 9.54883 4.35357 9.35356C4.54884 9.1583 4.54884 8.84172 4.35357 8.64646L3.20712 7.50001L3.85357 6.85356L6.85357 3.85355ZM10.0993 13.1936L9.12959 11.5775L11.1464 9.56067L11.4697 11.8232L10.0993 13.1936ZM3.42251 5.87041L5.43935 3.85356L3.17678 3.53034L1.80638 4.90074L3.42251 5.87041ZM2.35356 10.3535C2.54882 10.1583 2.54882 9.8417 2.35356 9.64644C2.1583 9.45118 1.84171 9.45118 1.64645 9.64644L0.646451 10.6464C0.451188 10.8417 0.451188 11.1583 0.646451 11.3535C0.841713 11.5488 1.1583 11.5488 1.35356 11.3535L2.35356 10.3535ZM3.85358 11.8536C4.04884 11.6583 4.04885 11.3417 3.85359 11.1465C3.65833 10.9512 3.34175 10.9512 3.14648 11.1465L1.14645 13.1464C0.95119 13.3417 0.951187 13.6583 1.14645 13.8535C1.34171 14.0488 1.65829 14.0488 1.85355 13.8536L3.85358 11.8536ZM5.35356 13.3535C5.54882 13.1583 5.54882 12.8417 5.35356 12.6464C5.1583 12.4512 4.84171 12.4512 4.64645 12.6464L3.64645 13.6464C3.45119 13.8417 3.45119 14.1583 3.64645 14.3535C3.84171 14.5488 4.1583 14.5488 4.35356 14.3535L5.35356 13.3535ZM9.49997 6.74881C10.1897 6.74881 10.7488 6.1897 10.7488 5.5C10.7488 4.8103 10.1897 4.25118 9.49997 4.25118C8.81026 4.25118 8.25115 4.8103 8.25115 5.5C8.25115 6.1897 8.81026 6.74881 9.49997 6.74881Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
    ),
    timeRelevant: 'morning'
  },
  
  // Afternoon/Midday
  { 
    id: 'destress', 
    label: 'Midday Destress', 
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/>
      </svg>
    ),
    timeRelevant: 'afternoon'
  },
  { 
    id: 'recharge', 
    label: 'Recharge & Reset', 
    icon: (
 <svg width="22" height="22" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.7649 6.07596C14.9991 6.22231 15.0703 6.53079 14.9239 6.76495C14.4849 7.46743 13.9632 8.10645 13.3702 8.66305L14.5712 9.86406C14.7664 10.0593 14.7664 10.3759 14.5712 10.5712C14.3759 10.7664 14.0593 10.7664 13.8641 10.5712L12.6011 9.30817C11.805 9.90283 10.9089 10.3621 9.93375 10.651L10.383 12.3277C10.4544 12.5944 10.2961 12.8685 10.0294 12.94C9.76267 13.0115 9.4885 12.8532 9.41704 12.5865L8.95917 10.8775C8.48743 10.958 8.00036 10.9999 7.50001 10.9999C6.99965 10.9999 6.51257 10.958 6.04082 10.8775L5.58299 12.5864C5.51153 12.8532 5.23737 13.0115 4.97064 12.94C4.7039 12.8686 4.5456 12.5944 4.61706 12.3277L5.06625 10.651C4.09111 10.3621 3.19503 9.90282 2.3989 9.30815L1.1359 10.5712C0.940638 10.7664 0.624058 10.7664 0.428798 10.5712C0.233537 10.3759 0.233537 10.0593 0.428798 9.86405L1.62982 8.66303C1.03682 8.10643 0.515113 7.46742 0.0760677 6.76495C-0.0702867 6.53079 0.000898544 6.22231 0.235065 6.07596C0.469231 5.9296 0.777703 6.00079 0.924058 6.23496C1.40354 7.00213 1.989 7.68057 2.66233 8.2427C2.67315 8.25096 2.6837 8.25972 2.69397 8.26898C4.00897 9.35527 5.65537 9.99991 7.50001 9.99991C10.3078 9.99991 12.6564 8.5063 14.076 6.23495C14.2223 6.00079 14.5308 5.9296 14.7649 6.07596Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
    ),
    timeRelevant: 'afternoon'
  },
  
  // General (any time)
  { 
    id: 'stress', 
    label: 'Release Stress & Tension', 
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
      </svg>
    ),
    timeRelevant: 'all'
  },
  { 
    id: 'focus', 
    label: 'Improve Focus & Clarity', 
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <circle cx="12" cy="12" r="6"/>
        <circle cx="12" cy="12" r="2"/>
      </svg>
    ),
    timeRelevant: 'all'
  },
  
  // Evening
  { 
    id: 'sleep', 
    label: 'Prepare for Restful Sleep', 
    icon: (
<svg width="22" height="22" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.89998 0.499976C2.89998 0.279062 2.72089 0.0999756 2.49998 0.0999756C2.27906 0.0999756 2.09998 0.279062 2.09998 0.499976V1.09998H1.49998C1.27906 1.09998 1.09998 1.27906 1.09998 1.49998C1.09998 1.72089 1.27906 1.89998 1.49998 1.89998H2.09998V2.49998C2.09998 2.72089 2.27906 2.89998 2.49998 2.89998C2.72089 2.89998 2.89998 2.72089 2.89998 2.49998V1.89998H3.49998C3.72089 1.89998 3.89998 1.72089 3.89998 1.49998C3.89998 1.27906 3.72089 1.09998 3.49998 1.09998H2.89998V0.499976ZM5.89998 3.49998C5.89998 3.27906 5.72089 3.09998 5.49998 3.09998C5.27906 3.09998 5.09998 3.27906 5.09998 3.49998V4.09998H4.49998C4.27906 4.09998 4.09998 4.27906 4.09998 4.49998C4.09998 4.72089 4.27906 4.89998 4.49998 4.89998H5.09998V5.49998C5.09998 5.72089 5.27906 5.89998 5.49998 5.89998C5.72089 5.89998 5.89998 5.72089 5.89998 5.49998V4.89998H6.49998C6.72089 4.89998 6.89998 4.72089 6.89998 4.49998C6.89998 4.27906 6.72089 4.09998 6.49998 4.09998H5.89998V3.49998ZM1.89998 6.49998C1.89998 6.27906 1.72089 6.09998 1.49998 6.09998C1.27906 6.09998 1.09998 6.27906 1.09998 6.49998V7.09998H0.499976C0.279062 7.09998 0.0999756 7.27906 0.0999756 7.49998C0.0999756 7.72089 0.279062 7.89998 0.499976 7.89998H1.09998V8.49998C1.09998 8.72089 1.27906 8.89997 1.49998 8.89997C1.72089 8.89997 1.89998 8.72089 1.89998 8.49998V7.89998H2.49998C2.72089 7.89998 2.89998 7.72089 2.89998 7.49998C2.89998 7.27906 2.72089 7.09998 2.49998 7.09998H1.89998V6.49998ZM8.54406 0.98184L8.24618 0.941586C8.03275 0.917676 7.90692 1.1655 8.02936 1.34194C8.17013 1.54479 8.29981 1.75592 8.41754 1.97445C8.91878 2.90485 9.20322 3.96932 9.20322 5.10022C9.20322 8.37201 6.82247 11.0878 3.69887 11.6097C3.45736 11.65 3.20988 11.6772 2.96008 11.6906C2.74563 11.702 2.62729 11.9535 2.77721 12.1072C2.84551 12.1773 2.91535 12.2458 2.98667 12.3128L3.05883 12.3795L3.31883 12.6045L3.50684 12.7532L3.62796 12.8433L3.81491 12.9742L3.99079 13.089C4.11175 13.1651 4.23536 13.2375 4.36157 13.3059L4.62496 13.4412L4.88553 13.5607L5.18837 13.6828L5.43169 13.7686C5.56564 13.8128 5.70149 13.8529 5.83857 13.8885C5.94262 13.9155 6.04767 13.9401 6.15405 13.9622C6.27993 13.9883 6.40713 14.0109 6.53544 14.0298L6.85241 14.0685L7.11934 14.0892C7.24637 14.0965 7.37436 14.1002 7.50322 14.1002C11.1483 14.1002 14.1032 11.1453 14.1032 7.50023C14.1032 7.25044 14.0893 7.00389 14.0623 6.76131L14.0255 6.48407C13.991 6.26083 13.9453 6.04129 13.8891 5.82642C13.8213 5.56709 13.7382 5.31398 13.6409 5.06881L13.5279 4.80132L13.4507 4.63542L13.3766 4.48666C13.2178 4.17773 13.0353 3.88295 12.8312 3.60423L12.6782 3.40352L12.4793 3.16432L12.3157 2.98361L12.1961 2.85951L12.0355 2.70246L11.8134 2.50184L11.4925 2.24191L11.2483 2.06498L10.9562 1.87446L10.6346 1.68894L10.3073 1.52378L10.1938 1.47176L9.95488 1.3706L9.67791 1.2669L9.42566 1.1846L9.10075 1.09489L8.83599 1.03486L8.54406 0.98184ZM10.4032 5.30023C10.4032 4.27588 10.2002 3.29829 9.83244 2.40604C11.7623 3.28995 13.1032 5.23862 13.1032 7.50023C13.1032 10.593 10.596 13.1002 7.50322 13.1002C6.63646 13.1002 5.81597 12.9036 5.08355 12.5522C6.5419 12.0941 7.81081 11.2082 8.74322 10.0416C8.87963 10.2284 9.10028 10.3497 9.34928 10.3497C9.76349 10.3497 10.0993 10.0139 10.0993 9.59971C10.0993 9.24256 9.84965 8.94373 9.51535 8.86816C9.57741 8.75165 9.63653 8.63334 9.6926 8.51332C9.88358 8.63163 10.1088 8.69993 10.35 8.69993C11.0403 8.69993 11.6 8.14028 11.6 7.44993C11.6 6.75976 11.0406 6.20024 10.3505 6.19993C10.3853 5.90487 10.4032 5.60464 10.4032 5.30023Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
    ),
    timeRelevant: 'evening'
  },
  { 
    id: 'gratitude', 
    label: 'Cultivate Gratitude', 
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    ),
    timeRelevant: 'evening'
  }
];
const handleIntentionScroll = (direction) => {
  const maxIndex = Math.ceil(intentionOptions.length / 4) - 1;
  
  if (direction === 'next' && intentionScrollIndex < maxIndex) {
    setIntentionScrollIndex(prev => prev + 1);
  } else if (direction === 'prev' && intentionScrollIndex > 0) {
    setIntentionScrollIndex(prev => prev - 1);
  }
};

const visibleIntentions = intentionOptions.slice(
  intentionScrollIndex * 4,
  (intentionScrollIndex * 4) + 4
);

const hasMore = intentionScrollIndex < Math.ceil(intentionOptions.length / 4) - 1;
const hasPrev = intentionScrollIndex > 0;
useEffect(() => {
  if (transcriptRef.current) {
    // Scroll to bottom with smooth behavior
    transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }
}, [transcript]);

const [currentSessionId, setCurrentSessionId] = useState(null);
const [sessionProgress, setSessionProgress] = useState({
  scriptLines: [],
  currentIndex: 0,
  ambientTime: 0,
  timeOfDay: '',
  isPaused: false,
  hasStarted: false
});
useEffect(() => {
  const initService = async () => {
    try {
      const { default: FirestorePersistentStorageService } = await import('./services/FirestorePersistentStorageService');
      
      const service = new FirestorePersistentStorageService();
      await service.initialize();
      
      setFirestoreService(service);
      window.firestoreService = service;
      
      console.log('✅ Firestore service initialized');
    } catch (error) {
      console.error('❌ Failed to initialize service:', error);
    }
  };

  initService();
}, []);
const loadSessionState = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user || !firestoreService) return null;
    
    const userData = await firestoreService.getUserData();
    const sessions = userData?.sessions || {};
    
    // Find active session for today + timeOfDay
    const sessionKey = `${timeOfDay}-${new Date().toDateString()}`;
    
    for (const [key, sessionData] of Object.entries(sessions)) {
      const savedTime = new Date(sessionData.timestamp);
      const now = new Date();
      const isSameDay = savedTime.toDateString() === now.toDateString();
      const isSameTimeOfDay = sessionData.timeOfDay === timeOfDay;
      
      // ✅ Only restore if SAME day + SAME time period AND session is incomplete
      if (isSameDay && isSameTimeOfDay && sessionData.hasStarted && sessionData.currentIndex < sessionData.scriptLines.length) {
        log(`🔄 Found saved session: ${key}`, 'session');
        setCurrentSessionId(key);
        return sessionData;
      }
    }
    
    // ✅ No valid saved session - start fresh
    return null;
  } catch (err) {
    log(`No saved session available`, 'info');
    return null;
  }
};

const clearSessionState = async () => {
  if (!currentSessionId || !firestoreService) return;
  
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    
    await firestoreService.updateUserData({
      [`sessions.${currentSessionId}`]: deleteField()
    });
    
    log(`✅ Session cleared`, 'session');
    setCurrentSessionId(null);
  } catch (err) {
    log(`Could not clear session: ${err.message}`, 'warn');
  }
};
const setupMediaSession = () => {
  if ('mediaSession' in navigator) {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: `${selectedIntention?.label || 'Meditation'} - ${timeOfDay}`,
      artist: displayName || 'Preptune Guide',
      album: 'Meditation Session',
      artwork: [
        { 
          src: backgroundImage || '/meditation-icon.png', 
          sizes: '512x512', 
          type: 'image/png' 
        }
      ]
    });

    // Play control
    navigator.mediaSession.setActionHandler('play', () => {
      if (ambientAudioRef.current) {
        ambientAudioRef.current.play();
        setIsPlaying(true);
      }
    });

    // Pause control
    navigator.mediaSession.setActionHandler('pause', () => {
      if (ambientAudioRef.current) {
        ambientAudioRef.current.pause();
        setIsPlaying(false);
      }
    });

    log('✅ Media Session ready', 'media');
  }
};

// ============================================
// IMPROVED REFLECTION START
// Loads saved session OR starts fresh
// ============================================

const runReflection = async () => {
  setHasStartedPlaying(true);

  if (isMicActive) {
    log('Mic already active', 'info');
    return;
  }
  
  log('🎧 STARTING REFLECTION', 'start');
  abortControllerRef.current = new AbortController();
  setIsPlaying(true);
  setTranscript([]);

  // Generate unique session ID
  const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  setCurrentSessionId(newSessionId);

  setLoadingMessage(`Crafting your meditation...`);

  try {
    // ✅ CALCULATE TIMING VARIABLES FIRST (before they're needed)
    const estimatedDuration = ambientDurationRef.current || 600;
    const targetExchanges = Math.max(30, Math.floor(estimatedDuration / 7));
    
    // ✅ CHECK FOR SAVED SESSION (same day, same time)
    const savedSession = await loadSessionState();

    
    if (savedSession && savedSession.scriptLines.length > 0) {
      log(`🔄 Restoring session from ${Math.floor(savedSession.currentIndex)}/${savedSession.scriptLines.length}`, 'resume');
      
      // Restore state
      pausedStateRef.current = {
        scriptLines: savedSession.scriptLines,
        currentIndex: savedSession.currentIndex,
        isPaused: false
      };
      
      if (savedSession.currentEntry) {
        setCurrentEntry(savedSession.currentEntry);
      }
      
      setLoadingMessage('');
      await startAmbientMusic();
      
      if (ambientAudioRef.current && savedSession.ambientTime) {
        ambientAudioRef.current.currentTime = savedSession.ambientTime;
      }
      
      setCurrentStatus('Continuing your meditation...');
      
      // Continue from where they left off
      const scriptText = savedSession.scriptLines.join('\n');
      await playScript(scriptText, savedSession.currentIndex);
      return;
    }

    // ✅ NO SAVED SESSION - START FRESH
    log('Starting fresh meditation session', 'start');
    
    await startAmbientMusic();
    
      setupMediaSession();

    setLoadingMessage('');
    setCurrentStatus('Your meditation is playing...');
    
    const unlockedEntries = entries.filter(entry => !isEntryLocked(entry.id));

    if (unlockedEntries.length === 0) {
      alert('No journal entries available for reflection.');
      setIsPlaying(false);
      return;
    }

    const selectedEntry = unlockedEntries[Math.floor(Math.random() * unlockedEntries.length)];
    setCurrentEntry(selectedEntry);
    log(`📖 Selected entry: "${selectedEntry.title}"`, 'entry');

    // ✅ GENERATE AI SCRIPT (based on intention)
    const prompt = generateMeditationReflectionPrompt(
      displayName, 
      timeOfDay, 
      selectedEntry, 
      isEntryLocked, 
      timeSinceLastVisit, 
      entries,
      selectedIntention
    );

const script = await generateContent(
  prompt, 
  `You are a master meditation guide creating deeply personal sessions.

**IMPORTANT:** Generate a LONG meditation script (140-160+ exchanges).
This will fill most of the meditation time. The system will handle the closing automatically.

Every line is natural, warm, genuine. Two guides having a real conversation.
Guide the user to CLOSE their eyes early in the session, and OPEN them at the very end.
Use their actual journal content. Make them feel known.
Build gradually with varied approaches—don't repeat patterns.

DO NOT include a closing section - the system generates that dynamically based on music timing.`
);


// ✅ Add verification after generation
const exchanges = script.split('\n').filter(line => 
  line.match(/^(Guide|Companion):/i)
).length;

const expectedMin = Math.floor(targetExchanges * 0.8); // 80% of target minimum
console.log(`📊 Generated ${exchanges} exchanges (target: ${targetExchanges}, minimum: ${expectedMin})`);

// ✅ WARN if script is too short
if (exchanges < expectedMin) {
  log(`⚠️ WARNING: Script only has ${exchanges} exchanges (target: ${targetExchanges}, minimum: ${expectedMin})`, 'warn');
  console.warn(`Script may be too short. Only ${exchanges} exchanges generated when ${targetExchanges} were needed.`);
}

// ✅ Log success if we hit target
if (exchanges >= targetExchanges) {
  log(`✅ Script length perfect: ${exchanges} exchanges for ${Math.round(estimatedDuration)}s ambient`, 'success');
}
console.log(`📊 Generated ${exchanges} exchanges`);


// ✅ WARN if script is too short
if (exchanges < 100) {
  log(`⚠️ WARNING: Script only has ${exchanges} exchanges (target: 120-150+)`, 'warn');
  console.warn(`Script may be too short. Only ${exchanges} exchanges generated.`);
}

    log(`✅ Script generated (${script.split('\n').length} lines)`, 'success');

    if (abortControllerRef.current?.signal.aborted) return;

    setTranscript([]);
    pausedStateRef.current = {
      scriptLines: script.split('\n').filter(l => l.trim()),
      currentIndex: 0,
      isPaused: false
    };
    
    // ✅ PLAY FULL SESSION
    await playScript(script);

  } catch (error) {
    if (error.name === 'AbortError') {
      log('🛑 Reflection stopped by user', 'warn');
      return;
    }
    log(`❌ Reflection error: ${error.message}`, 'error');
    setIsPlaying(false);
  }
};

// ============================================
// IMPROVED TOGGLE PLAY/PAUSE
// Only start fresh session once
// ============================================

const togglePlayPause = async () => {
  // Initialize AudioContext on first interaction
  if (!audioContextRef.current) {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      log('✅ AudioContext created', 'audio');
    } catch (e) {
      log(`❌ AudioContext failed: ${e.message}`, 'error');
    }
  }

  if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
    await audioContextRef.current.resume();
  }

  if (isPlaying) {
    // User is pausing
    await pauseReflection();
  } else if (isPaused) {
    // User is resuming from pause
    await resumeReflection();
  } else {
    // ✅ Show intention modal before starting fresh
    if (!hasStartedPlaying && !selectedIntention) {
      setShowIntentionModal(true);
      return;
    }
      // ✅ SCHEDULE NOTIFICATIONS ON FIRST PLAY
    await scheduleFirstTimeMeditationNotifications();
    
    // ✅ Start fresh session (will check for saved session internally)
    await runReflection();
  }
};

// ============================================
// COMPONENT MOUNT - Reset on app restart
// ============================================

useEffect(() => {
  // On component mount, reset session state
  // This ensures app restart = fresh meditation experience
  pausedStateRef.current = { scriptLines: [], currentIndex: 0, isPaused: false };
  setCurrentSessionId(null);
  setHasStartedPlaying(false);
  setIsPlaying(false);
  setIsPaused(false);
  
  log('📱 Component mounted - session reset for fresh start', 'init');
  
  return () => {
    // Cleanup on unmount
    stopReflection();
  };
}, []);

const generateWelcomeBackPrompt = (userName, timeOfDay, lastTopic, progressPercent) => {
  const hour = new Date().getHours();
  
  // Time-specific context
  let timeContext = '';
  if (timeOfDay === 'morning') {
    if (hour >= 5 && hour < 7) {
      timeContext = 'early in the morning, before the world wakes up';
    } else if (hour >= 7 && hour < 9) {
      timeContext = 'in the heart of morning, as the day begins';
    } else if (hour >= 9 && hour < 12) {
      timeContext = 'late morning, as the day unfolds';
    }
  } else if (timeOfDay === 'afternoon') {
    if (hour >= 12 && hour < 14) {
      timeContext = 'in the midday light';
    } else if (hour >= 14 && hour < 17) {
      timeContext = 'in the afternoon calm';
    } else if (hour >= 17 && hour < 18) {
      timeContext = 'as afternoon turns to evening';
    }
  } else if (timeOfDay === 'evening') {
    if (hour >= 18 && hour < 20) {
      timeContext = 'in early evening, as the day winds down';
    } else if (hour >= 20 && hour < 22) {
      timeContext = 'in the quiet of evening';
    } else if (hour >= 22 || hour < 5) {
      timeContext = 'late at night, in the deep stillness';
    }
  }

  const progressDescription = progressPercent < 30 ? 'just getting started' :
                              progressPercent < 70 ? 'halfway through your reflection' :
                              'near the end of your session';

  return `Generate a brief, warm WELCOME BACK message for ${userName} who paused their ${timeOfDay} meditation session.

**CONTEXT:**
- Time: ${timeContext}
- They were ${progressDescription}
- Last discussing: "${lastTopic}"
- This is their ${timeOfDay} session (only 1 per day)

**TONE GUIDELINES:**
${timeOfDay === 'morning' ? `
- ENERGIZING yet GROUNDING - they're preparing for their day
- Acknowledge the power of this morning moment
- Help them feel ready and centered
- Reference the fresh energy of morning
` : timeOfDay === 'afternoon' ? `
- REBALANCING and CENTERING - midday reset
- Acknowledge they're taking time to pause and recalibrate
- Help them reconnect with their center
- Reference the midday stillness
` : `
- SOFTENING and RELEASING - end-of-day peace
- Acknowledge the transition from day to night
- Help them let go and settle
- Reference the evening quiet and darkness
- ${hour >= 22 || hour < 5 ? 'EXTRA GENTLE - it\'s very late, honor their need for rest' : ''}
`}

**STRUCTURE:**
1. Welcome back naturally: "Welcome back, ${userName}" or "${userName}, you're back" or "Hey ${userName}, glad you returned"
2. Acknowledge the time: Mention ${timeContext} naturally
3. Brief reconnection: "We were exploring..." (10-15 words about the last topic)
4. Smooth transition: "Let's continue from where we paused..."

**LENGTH:** 30-40 words total

**EXAMPLES:**
Morning: "Welcome back, ${userName}. ${timeContext}, you've returned to your morning practice. We were exploring the weight you've been carrying. Take a breath. Let's continue where we left off."

Evening: "${userName}, you're back. ${timeContext}, this is your time to let go. We were sitting with your day's challenges. Breathe. Let's continue gently from where we paused."

Output ONLY the welcome back message (no quotes, no labels, natural speech).`;
};
const handleJoinClick = () => {
  if (isMicActive || interactionMode) {
    // User is leaving conversation
    leaveConversation();
  } else {
    // Show modal to choose voice or text
    setShowJoinModal(true);
  }
};

const cancelJoinModal = () => {
  setShowJoinModal(false);
};

const selectInteractionMode = async (mode) => {
  log(`📱 User selected interaction mode: ${mode}`, 'interaction');
  
  // ✅ Step 1: Close the join modal immediately
  setShowJoinModal(false);
  setInteractionMode(mode);
  
  // ✅ Step 2: Pause the meditation reflection
  const wasPlayingReflection = isPlaying && !pausedStateRef.current.isPaused;
  if (wasPlayingReflection) {
    pausedStateRef.current.isPaused = true;
    setIsPaused(true);
    log('✅ Reflection paused for user interaction', 'pause');
  }
  
  // ✅ Step 3: Ensure ambient music continues (DON'T stop it)
  if (!ambientAudioRef.current) {
    await startAmbientMusic();
  } else if (ambientAudioRef.current.paused) {
    try {
      await ambientAudioRef.current.play();
      log('✅ Ambient resumed', 'ambient');
    } catch (err) {
      log(`⚠️ Could not resume ambient: ${err.message}`, 'warn');
    }
  }
  
  // ✅ Step 4: Small delay, then host greeting + interaction starts
  setTimeout(async () => {
    try {
      // Get random greeting
      const greetings_map = {
        morning: "Good morning",
        afternoon: "Good afternoon",
        evening: "Good evening"
      };
      
      if (mode === 'voice') {
        log('🎤 Activating voice mode', 'mic');
        
        const greetingVariations = [
          `${greetings_map[timeOfDay]}, ${displayName}. I'm listening.`,
          `Hey ${displayName}! Go ahead, I'm here.`,
          `${greetings_map[timeOfDay]}, ${displayName}. What's on your mind?`,
          `Hi ${displayName}! Whenever you're ready.`,
          `${greetings_map[timeOfDay]}, ${displayName}. I'm all ears.`,
        ];
        
        const greeting = greetingVariations[Math.floor(Math.random() * greetingVariations.length)];
        
        // Speak greeting
        isHostSpeakingRef.current = true;
        await speakChunk(greeting, 'guide');
        isHostSpeakingRef.current = false;
        
        // THEN activate microphone
        log('🎤 Starting microphone after greeting', 'mic');
        await toggleMicrophone();
        startInactivityTimer();
        
      } else if (mode === 'text') {
        log('💬 Activating text mode', 'text');
        
        const greetingVariations = [
          `${greetings_map[timeOfDay]}, ${displayName}. What's on your mind?`,
          `Hey ${displayName}! Type away - I'm listening.`,
          `${greetings_map[timeOfDay]}, ${displayName}. What would help?`,
          `Hi ${displayName}! What would you like to explore?`,
          `${greetings_map[timeOfDay]}, ${displayName}. Share what you need.`,
        ];
        
        const greeting = greetingVariations[Math.floor(Math.random() * greetingVariations.length)];
        
        // Speak greeting
        isHostSpeakingRef.current = true;
        await speakChunk(greeting, 'guide');
        isHostSpeakingRef.current = false;
        
        // Clear transcript to make room for user input
        setTranscript([]);
        
        // Make sure text input is visible and focused
        setTimeout(() => {
          const inputEl = document.querySelector('input[placeholder="Type your message..."]');
          if (inputEl) {
            inputEl.focus();
            log('✅ Text input focused and ready', 'text');
          }
        }, 300);
      }
      
      log('✅ User interaction mode fully activated', 'interaction');
      
    } catch (error) {
      log(`❌ Error activating interaction mode: ${error.message}`, 'error');
      // Fallback: just show the input/mic anyway
      if (mode === 'voice') {
        setIsMicActive(true);
        setMicStatus('listening');
      } else {
        setTranscript([]);
      }
    }
  }, 300);
};

const leaveConversation = async () => {
  log('👋 User leaving conversation', 'leave');
  
  // ✅ Stop microphone if active
  if (isMicActive) {
    isMicActiveRef.current = false;
    setIsMicActive(false);
    
    // Send any final accumulated text
    if (accumulatedTranscriptRef.current.trim().length > 3) {
      log(`📤 Sending final accumulated text: "${accumulatedTranscriptRef.current.trim()}"`, 'mic');
      await handleUserQuestion(accumulatedTranscriptRef.current.trim());
      accumulatedTranscriptRef.current = '';
    }
    
    setIsListening(false);
    setMicStatus('');
    setUserTranscript('');
    
    safeStopRecognition(azureRecognizerRef);
    
    // Clear all timers
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }
  
  // ✅ Exit interaction mode
  setInteractionMode(null);
  setTextInput('');
  
  log('✅ Exited interaction mode', 'interaction');
  
  // ✅ Check ambient status
  const ambientEnded = ambientAudioRef.current && ambientAudioRef.current.ended;
  const ambientPaused = ambientAudioRef.current && ambientAudioRef.current.paused;
  
  // ✅ Determine what to do next
  if (ambientEnded) {
    // Ambient has naturally ended
    log('🎵 Ambient ended - showing farewell', 'end');
    
    // Stop all audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    // Show farewell screen
    showFarewellCard();
    
  } else if (pausedStateRef.current.isPaused && pausedStateRef.current.scriptLines.length > 0) {
    // Meditation was paused - resume it naturally
    log('▶️ Resuming paused meditation after user leaves', 'resume');
    
    // Make sure ambient is playing
    if (ambientPaused) {
      try {
        await ambientAudioRef.current.play();
        log('✅ Ambient resumed', 'ambient');
      } catch (err) {
        log(`⚠️ Could not resume ambient: ${err.message}`, 'warn');
      }
    }
    
    // Resume the reflection naturally
    await resumeReflection();
    
  } else {
    // Meditation is still running naturally
    log('▶️ Meditation continuing in background', 'info');
    
    // Make sure ambient is playing
    if (ambientPaused) {
      try {
        await ambientAudioRef.current.play();
        log('✅ Ambient resumed', 'ambient');
      } catch (err) {
        log(`⚠️ Could not resume ambient: ${err.message}`, 'warn');
      }
    }
  }
};

const handleTextSubmit = async () => {
  // ✅ Guard against spam or invalid input
  if (!textInput.trim() || isSendingText) return;
  
  if (!interactionMode) {
    log('⚠️ Text submitted but not in text mode', 'warn');
    return;
  }
  
  const userMessage = textInput.trim();
  setTextInput(''); // Clear immediately
  setIsSendingText(true);
  
  log(`💬 User text input: ${userMessage}`, 'text');
  
  // ✅ Add user message to transcript (in green)
  setTranscript(prev => [...prev, { 
    voice: 'user', 
    text: userMessage, 
    timestamp: Date.now() 
  }]);
  
  // Process the question
  await handleUserQuestion(userMessage);
  
  // ✅ Keep text mode active for next message
  setIsSendingText(false);
  
  // Keep focus on input for next message
  setTimeout(() => {
    const inputEl = document.querySelector('input[placeholder="Type your message..."]');
    if (inputEl) {
      inputEl.focus();
      inputEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, 200);
};
const safeStopRecognition = (ref) => {
  try {
    if (ref?.current?.stopContinuousRecognitionAsync) {
      ref.current.stopContinuousRecognitionAsync();
    } else if (ref?.current?.stop) {
      ref.current.stop();
    }
  } catch (e) {
    console.log('Stop error:', e.message);
  }
};

const safeStartRecognition = (ref) => {
  try {
    if (ref?.current?.startContinuousRecognitionAsync) {
      ref.current.startContinuousRecognitionAsync();
    } else if (ref?.current?.start) {
      ref.current.start();
    }
  } catch (e) {
    console.log('Start error:', e.message);
  }
};

const greetings = {
  morning: "Good morning",
  afternoon: "Good afternoon",
  evening: "Good evening"
};
useEffect(() => {
  let isMounted = true;
  
  const loadUserName = async () => {
    if (!firestoreService?.isReady?.()) {
      return;
    }
    
    setIsLoadingUserName(true);
    
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser || !isMounted) {
        setFirestoreUserName(null);
        setIsLoadingUserName(false);
        return;
      }

      const settings = await firestoreService.getUserSettings();
      
      if (settings?.userName?.trim()) {
        if (isMounted) {
          setFirestoreUserName(settings.userName);
        }
      } else {
        if (isMounted) {
          setFirestoreUserName(null);
        }
      }
    } catch (err) {
      if (isMounted) {
        setFirestoreUserName(null);
      }
    } finally {
      if (isMounted) {
        setIsLoadingUserName(false);
      }
    }
  };

  loadUserName();
  
  return () => {
    isMounted = false;
  };
}, [firestoreService]);
// ============================================
// ALSO UPDATE THE AUTH LISTENER (around line 400):
// ============================================
useEffect(() => {
  let unsubscribe = null;

  const setupAuthListener = async () => {
    try {
      const auth = getAuth();
      
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user && firestoreService?.isReady?.()) {
          try {
            const settings = await firestoreService.getUserSettings();
            if (settings?.userName?.trim()) {
              setFirestoreUserName(settings.userName);
            }
          } catch (error) {
            // Silent fail
          }
        } else {
          setFirestoreUserName(null);
        }
      });
    } catch (error) {
      // Silent fail
    }
  };

  setupAuthListener();

  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
}, [firestoreService]);
useEffect(() => {
  // Initialize Firestore storage service
  const initStorage = async () => {
    try {
      // Get the storage service instance (assuming it's passed as a prop or imported)
      // If you're importing it globally, use:
      // import storageService from './path/to/FirestorePersistentStorageService';
      
      // For now, create a simple wrapper around your Firestore service
      const firestoreStorage = {
        get: async (key) => {
          try {
            const userData = await window.firestoreService.getUserData();
            const sessionData = userData?.audioSessions?.[key];
            return sessionData ? { value: sessionData } : null;
          } catch (err) {
            console.log('Storage get error:', err);
            return null;
          }
        },
        
        set: async (key, value) => {
          try {
            await window.firestoreService.updateUserData({
              [`audioSessions.${key}`]: value
            });
            return { success: true };
          } catch (err) {
            console.log('Storage set error:', err);
            return null;
          }
        },
        
        delete: async (key) => {
          try {
            await window.firestoreService.updateUserData({
              [`audioSessions.${key}`]: deleteField()
            });
            return { success: true };
          } catch (err) {
            console.log('Storage delete error:', err);
            return null;
          }
        }
      };
      
      // Make it available globally
      window.storage = firestoreStorage;
      setStorageService(firestoreStorage);
      
      log('✅ Firestore storage initialized', 'storage');
    } catch (err) {
      log('⚠️ Storage initialization failed, continuing without persistence', 'warn');
    }
  };
  
  initStorage();
}, []);

// Load suggestions on mount
// Add event listeners to ambient audio
useEffect(() => {
  if (ambientAudioRef.current) {
    const audio = ambientAudioRef.current;
    
    audio.addEventListener('loadstart', () => log('🎵 Ambient loading...', 'audio'));
    audio.addEventListener('canplay', () => log('✅ Ambient ready to play', 'audio'));
    audio.addEventListener('playing', () => log('▶️ Ambient playing', 'audio'));
    audio.addEventListener('pause', () => log('⏸️ Ambient paused', 'audio'));
    audio.addEventListener('ended', () => log('🔚 Ambient ended', 'audio'));
    audio.addEventListener('error', (e) => {
      log(`❌ Ambient error: ${e.target.error?.message || 'Unknown'}`, 'error');
      console.error('Audio error details:', e.target.error);
    });
  }
}, []);
useEffect(() => {
  // Only load suggestions after user has started playing
  if (!hasStartedPlaying) return;
  
  const loadSuggestions = async () => {
    setIsLoadingSuggestions(true);
    
    // Show skeleton cards immediately - wait 4 seconds before showing real content
    setTimeout(async () => {
      const topics = await generateSuggestionTopics();
      setSuggestionTopics(topics);
      log('✅ Suggestion topics loaded', 'info');
      setIsLoadingSuggestions(false);
    }, 4000);
  };
  
  loadSuggestions();
}, [hasStartedPlaying, entries.length, userName]);
// Replace the time calculation in the first useEffect with this:
useEffect(() => {
  const loadLastVisit = async () => {
    try {
      if (!window.storage || typeof window.storage.get !== 'function') {
        return;
      }
      
      const result = await window.storage.get(`last-visit-${displayName}`);
      if (result && result.value) {
        const lastTime = parseInt(result.value);
        setLastVisitTime(lastTime);
        
        // Calculate time difference
        const now = Date.now();
        const diffMs = now - lastTime;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        let timeText = '';
        
        if (diffMins < 5) {
          timeText = 'just a few moments';
        } else if (diffMins < 30) {
          timeText = 'a little while';
        } else if (diffMins < 60) {
          timeText = 'about half an hour';
        } else if (diffHours === 1) {
          timeText = 'about an hour';
        } else if (diffHours < 3) {
          timeText = 'a couple of hours';
        } else if (diffHours < 6) {
          timeText = 'a few hours';
        } else if (diffHours < 12) {
          timeText = 'several hours';
        } else if (diffHours < 24) {
          timeText = 'earlier today';
        } else if (diffDays === 1) {
          timeText = 'yesterday';
        } else if (diffDays === 2) {
          timeText = 'a couple of days';
        } else if (diffDays < 7) {
          timeText = 'a few days';
        } else if (diffDays < 14) {
          timeText = 'about a week';
        } else if (diffDays < 30) {
          timeText = 'a couple of weeks';
        } else {
          timeText = 'a while';
        }
        
        setTimeSinceLastVisit(timeText);
      }
    } catch (err) {
      log('No previous visit found or storage unavailable', 'info');
    }
  };
  
  loadLastVisit();
}, [userName]);

// Save visit time when component mounts:
useEffect(() => {
  const saveVisitTime = async () => {
    try {
      if (!window.storage || typeof window.storage.set !== 'function') {
        return;
      }
      
      await window.storage.set(`last-visit-${displayName}`, Date.now().toString());
    } catch (err) {
      log('Could not save visit time, continuing without it', 'warn');
    }
  };
  
  saveVisitTime();
}, [userName]);
// ============================================
// MEDITATION PROMPT - NO EXCHANGE LIMITS
// ============================================

const generateMeditationReflectionPrompt = (displayName, timeOfDay, selectedEntry, isEntryLocked, timeSinceLastVisit, entries, userIntention) => {
  const hour = new Date().getHours();
  const now = new Date();
  
  // ✅ Calculate how long this session should be based on ambient duration
  const estimatedDuration = ambientDurationRef.current || 600; // Default 10 min
  const wrapUpTime = estimatedDuration - 45; // Start wrapping 45s before end
  
// ✅ Calculate exchanges needed - each exchange averages 7-8 seconds (speaking + pause)
  // For 4 minutes (240s) we need about 30-35 exchanges
  // For 5 minutes (300s) we need about 40-45 exchanges
  const targetExchanges = Math.max(30, Math.floor(estimatedDuration / 7));
  
  log(`📊 Session timing: ${Math.round(estimatedDuration)}s ambient = ~${targetExchanges} exchanges needed (avg 7s/exchange)`, 'timing');
  
  const today = new Date().toDateString();
  const todayEntry = entries.find(e => new Date(e.date).toDateString() === today);
  const includeJournal = todayEntry && timeSinceLastVisit && (timeSinceLastVisit.includes('hour') || timeSinceLastVisit.includes('moment'));
  
  const journalContext = includeJournal 
    ? `\n\nRECENT JOURNAL (subtle context only):
    "${todayEntry.title}" (${todayEntry.mood})
    Snippet: ${todayEntry.content.substring(0, 100)}...
    
    Use ONLY if naturally relevant. Don't force it.`
    : '';

  // ✅ INTENTION-BASED GUIDANCE
  const intentionGuidance = getIntentionGuidance(userIntention);

return `You are creating a NATURAL, TIMED meditation for ${displayName}.

**CRITICAL TIMING:**
- Ambient music duration: ${Math.round(estimatedDuration)} seconds (${Math.floor(estimatedDuration / 60)} minutes ${Math.round(estimatedDuration % 60)} seconds)
- You MUST generate AT LEAST ${targetExchanges} exchanges (aim for ${Math.floor(targetExchanges * 1.2)})
- Each exchange should be 15-25 words, spoken naturally
- System will automatically trigger wrap-up at ${Math.round(wrapUpTime)}s
- DO NOT write a closing - system handles that

**PACING:**
- Each Guide/Companion turn = ~7 seconds (speaking + pause)
- ${targetExchanges} exchanges × 7 seconds = ${Math.round(targetExchanges * 7)}s total
- This matches the ${Math.round(estimatedDuration)}s ambient track
- DO NOT rush - take your time with each instruction
**SELECTED INTENTION: ${userIntention?.label || 'General Meditation'}**

Every exchange should serve this intention. This is what ${displayName} chose.

**MEDITATION STRUCTURE (Total: ${targetExchanges}+ exchanges):**
1. **Opening (8-12 exchanges)** - Welcome, settle, orient to space
2. **Body Grounding (${Math.floor(targetExchanges * 0.2)} exchanges)** - Physical awareness related to intention
3. **Breath Work (${Math.floor(targetExchanges * 0.15)} exchanges)** - Breathing suited to intention  
4. **Deep Practice (${Math.floor(targetExchanges * 0.4)} exchanges)** - CORE work on intention - THIS IS THE LONGEST SECTION
5. **Integration (${Math.floor(targetExchanges * 0.15)} exchanges)** - Deepening and embodying
6. **Continuation (remaining ${Math.floor(targetExchanges * 0.1)} exchanges)** - Keep going naturally until system triggers wrap-up

**IMPORTANT:** Count your exchanges as you write. You should have ${targetExchanges} BEFORE the system wrap-up.
${intentionGuidance}
${journalContext}

**CRITICAL VOICE RULES:**
❌ NEVER write dialogue where ${displayName} responds
❌ NEVER write: "User:", "${displayName}:", or any user responses
✅ ONLY write: "Guide:" and "Companion:" speaking TO ${displayName}

**NATURAL MEDITATION LANGUAGE - CRITICAL:**

🚫 **AVOID THESE PATTERNS** (they sound robotic):
- "What do you hear right now?"
- "Notice the textures around you"
- "Feel the sensations of..."
- Starting with "Notice..." every time
- "Bring your awareness to..."
- Repetitive sentence structures
- Clinical/technical descriptions

✅ **USE THESE NATURAL PATTERNS** (like real instructors):
- Direct observations: "Your breath is there. Steady. Consistent."
- Simple invitations: "Let's take a breath together."
- Natural questions: "Where do you feel that?"
- Conversational flow: "Yeah, that makes sense."
- Personal address: "You know that feeling when..."
- Varied openings: Sometimes "Notice...", sometimes "Feel...", sometimes just statements
- Real human transitions: "So...", "And...", "Right now..."

**VARIATION EXAMPLES:**

Instead of:
"Notice the sensation of your breath moving in and out"

Write like:
"Your breath—it's always there. You don't have to do anything. Just let it be."

Instead of:
"Bring your awareness to the textures surrounding you"

Write like:
"What's touching your skin right now? The air? Your clothes? Just feel that."

Instead of:
"Notice how your body feels in this moment"

Write like:
"Check in with your body. Where's it tight? Where's it soft?"

**TONE REQUIREMENTS:**
- Warm and conversational, not clinical
- Sometimes poetic, sometimes direct
- Vary sentence length dramatically
- Use contractions naturally ("you're", "it's", "that's")
- Ask real questions (rhetorical, but genuine)
- Sound like TWO people having a real conversation
- One person can speak 2-3 times before the other responds
- Natural pauses and reactions

**BREATHING CUES - NATURAL:**
Don't always say "breathe in... breathe out..."

Vary it:
- "Take a breath with me."
- "Let's breathe."
- "Inhale. [pause] And let it go."
- "Feel your breath."
- "Just breathe for a second."

**PERSONALIZATION:**
- Use ${displayName}'s name every 8-12 exchanges
- Reference their intention: "${userIntention?.label}"
- Sound like you know them
- Be specific, not generic

**LENGTH TARGET: Generate ${targetExchanges} exchanges minimum**

Output ONLY the meditation script:
Guide: [natural speech TO ${displayName}]
Companion: [natural response, building on Guide]
Guide: [continues naturally]
...

GENERATE NOW - NATURAL, VARIED, ${targetExchanges}+ EXCHANGES:`;
};

// ============================================
// BREATHING WITH REALISTIC PAUSES (2-3 seconds)
// ============================================
const speakChunk = async (text, host) => {
  return new Promise(async (resolve) => {
    try {
      log(`🎙️ Speaking chunk: ${text.substring(0, 30)}...`, 'speech');

      // ✅ Add text to transcript
      setTranscript(prev => [...prev, {
        voice: host,
        text: text,
        timestamp: Date.now()
      }]);

      // ✅ CHECK FOR BREATHING CUES IN TEXT
      const hasInhale = /breathe in|inhale|breath in/i.test(text);
      const hasExhale = /breathe out|exhale|breath out|release/i.test(text);

      // ✅ ALWAYS generate audio - even in text/chat mode
      // Don't skip TTS based on mode - user should hear responses
      
      // ✅ USE SECURE API - NO MORE EXPOSED KEY
      const blob = await SecureAPI.callHumeTTSSecure(
        text,
        VOICE_CONFIG[host],
        0.99
      );
      
      if (blob.size === 0) {
        throw new Error('Blob is empty');
      }

      let url = URL.createObjectURL(blob);
      log(`✅ Blob created: ${blob.size} bytes`, 'blob');

      if (pausedStateRef.current.isPaused) {
        URL.revokeObjectURL(url);
        resolve();
        return;
      }

      const audio = new Audio(url);
      audio.playbackRate = 0.99;

      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }

      currentAudioRef.current = audio;

      let audioCompleted = false;

      const handleAudioEnd = () => {
        if (!audioCompleted) {
          audioCompleted = true;
          URL.revokeObjectURL(url);
          currentAudioRef.current = null;
          log(`✅ Chunk finished (${Math.round(audio.duration)}s)`, 'complete');
          
          audio.removeEventListener('ended', handleAudioEnd);
          audio.removeEventListener('error', handleError);
          
          // ✅ AFTER audio finishes, add breathing visualization if needed
          if (hasInhale && !hasExhale) {
            setTranscript(prev => [...prev, {
              voice: 'breath',
              text: '🫁 Breathing in · · ·',
              timestamp: Date.now(),
              isBreathCue: true
            }]);
          } else if (hasExhale && !hasInhale) {
            setTranscript(prev => [...prev, {
              voice: 'breath',
              text: '🫁 Breathing out · · ·',
              timestamp: Date.now(),
              isBreathCue: true
            }]);
          }
          
          resolve();
        }
      };

      const handleError = (error) => {
        if (!audioCompleted) {
          audioCompleted = true;
          URL.revokeObjectURL(url);
          currentAudioRef.current = null;
          log(`❌ Chunk error: ${error}`, 'error');
          
          audio.removeEventListener('ended', handleAudioEnd);
          audio.removeEventListener('error', handleError);
          
          resolve();
        }
      };

      audio.addEventListener('ended', handleAudioEnd);
      audio.addEventListener('error', handleError);

      audio.play()
        .then(() => {
          log(`▶️ Playing: ${text.substring(0, 40)}...`, 'play');
        })
        .catch((err) => {
          log(`❌ Play failed: ${err.message}`, 'error');
          handleError(err);
        });

    } catch (error) {
      log(`❌ TTS Error: ${error.message}`, 'error');
      resolve();
    }
  });
};
// ✅ WAIT FOR BREATHING (2-3 seconds realistic pause)
const waitForBreath = (type) => {
  const duration = type === 'inhale' ? 2500 : 3000; // Inhale 2.5s, exhale 3s
  return new Promise(resolve => setTimeout(resolve, duration));
};

const playScript = async (script, startIndex = 0) => {
  const lines = script.split('\n').filter(line => line.trim());
  pausedStateRef.current.scriptLines = lines;
  shouldWrapUpRef.current = false; // ✅ Reset wrap-up flag

  log(`🎬 Starting script with ${lines.length} lines`, 'script');

  const ambientDuration = ambientAudioRef.current?.duration || 0;
  log(`⏱️ Ambient duration: ${Math.round(ambientDuration)}s`, 'timer');

  await preloadNextLine(lines, 0);
  if (lines.length > 1) {
    await preloadNextLine(lines, 1);
  }
  
  let i = startIndex;

  while (i < lines.length) {
// ✅ Check: Should we wrap up now?
// BUT: Only if no audio is currently playing
if (shouldWrapUpRef.current && (!currentAudioRef.current || currentAudioRef.current.paused || currentAudioRef.current.ended)) {
  log('⏰ Time to wrap up - but wait for current audio to finish', 'wrap');
  
  // Wait for current chunk to finish if it's playing
  if (currentAudioRef.current && !currentAudioRef.current.paused) {
    await new Promise(resolve => {
      const checkFinish = () => {
        if (currentAudioRef.current && currentAudioRef.current.paused) {
          resolve();
        } else {
          setTimeout(checkFinish, 100);
        }
      };
      checkFinish();
    });
  }
  
  log('⏰ Current audio finished - NOW generating closing', 'wrap');
  await generateSmartWrapUp();
  break; // ✅ Exit the script loop after audio truly ends
}

    if (pausedStateRef.current.isPaused) {
      pausedStateRef.current.currentIndex = i;
      log(`⏸️ Paused at line ${i}/${lines.length}`, 'pause');
      await saveSessionState();
      return;
    }

    if (abortControllerRef.current?.signal.aborted) {
      log('🛑 Playback aborted', 'stop');
      return;
    }

    const currentLine = lines[i];
    const isGuide = currentLine.match(/^Guide:/i);
    const nextLine = i < lines.length - 1 ? lines[i + 1] : null;
    const nextIsCompanion = nextLine && nextLine.match(/^Companion:/i);

    if (isGuide && nextIsCompanion) {
      const combinedText = currentLine + '\n' + nextLine;
      pausedStateRef.current.currentIndex = i;

      const preloadPromise = preloadNextLine(lines, i + 2);
      await parseAndSpeakHosts(combinedText);

      if (pausedStateRef.current.isPaused) {
        pausedStateRef.current.currentIndex = i + 2;
        await saveSessionState();
        return;
      }

      await preloadPromise;

      const pauseDuration = 350 + Math.random() * 200;
      await new Promise(resolve => setTimeout(resolve, pauseDuration));

      i += 2;
    } else {
      const guideMatch = currentLine.match(/Guide:\s*(.+)/i);
      const companionMatch = currentLine.match(/Companion:\s*(.+)/i);

      if (guideMatch || companionMatch) {
        const voice = guideMatch ? 'guide' : 'therapist';
        const text = (guideMatch || companionMatch)[1].trim();

        pausedStateRef.current.currentIndex = i;

        const preloadPromise = preloadNextLine(lines, i + 1);
        await playAudio(text, voice);

        if (pausedStateRef.current.isPaused) {
          pausedStateRef.current.currentIndex = i + 1;
          await saveSessionState();
          return;
        }

        await preloadPromise;

        let pauseDuration = 300;
        if (text.length > 100) pauseDuration += 300;
        if (text.match(/\?$/)) pauseDuration += 500;
        if (text.match(/\.\.\.$/)) pauseDuration += 200;
        pauseDuration += Math.random() * 150;

        await new Promise(resolve => setTimeout(resolve, pauseDuration));
      }

      i++;
    }

    if (i % 5 === 0) {
      await saveSessionState();
    }
  }

  // ✅ If script finished naturally without wrap-up trigger
// ✅ If script finished naturally without wrap-up trigger
  if (!shouldWrapUpRef.current) {
    log('✅ Script finished naturally - generating smart wrap-up', 'complete');
    await generateSmartWrapUp();
  }

  log(`✅ Meditation script complete`, 'complete');
  
  // ✅ Wait for any ongoing audio to finish
  if (currentAudioRef.current && !currentAudioRef.current.paused) {
    log('⏳ Waiting for current audio to finish...', 'audio');
    await new Promise(resolve => {
      const checkAudio = setInterval(() => {
        if (!currentAudioRef.current || currentAudioRef.current.paused || currentAudioRef.current.ended) {
          clearInterval(checkAudio);
          resolve();
        }
      }, 100);
      // Safety timeout: 30 seconds max
      setTimeout(() => {
        clearInterval(checkAudio);
        resolve();
      }, 30000);
    });
  }
  
  // ✅ CRITICAL: Check if user is still chatting
  if (interactionMode) {
    log('💬 User is still in conversation - NOT showing farewell', 'info');
    // Keep ambient playing for continued conversation
    // Script is done but user can keep chatting
    return;
  }
  
  log('👋 Script complete, no active conversation - ending session', 'end');
  
  // Stop ambient cleanly
  if (ambientAudioRef.current) {
    fadeOutAmbient();
  }
  
  // Clear session
  await clearSessionState();
  pausedStateRef.current = { scriptLines: [], currentIndex: 0, isPaused: false };
  
  // Show farewell after a brief pause
  setTimeout(() => {
    if (!interactionMode) {
      showFarewellCard();
    }
  }, 1500);
};
const generateSmartWrapUp = async () => {
  // ✅ GUARD: Don't wrap up if user is chatting or still has time
  if (interactionMode) {
    log('⏸️ User is in conversation - skipping wrap up', 'warn');
    return;
  }
  
  // ✅ GUARD: Don't wrap up if current audio is still playing
  if (currentAudioRef.current && !currentAudioRef.current.paused && !currentAudioRef.current.ended) {
    log('⏸️ Audio still playing - waiting before wrap up', 'warn');
    await new Promise(resolve => {
      const wait = setInterval(() => {
        if (!currentAudioRef.current || currentAudioRef.current.paused || currentAudioRef.current.ended) {
          clearInterval(wait);
          resolve();
        }
      }, 100);
      // Safety timeout: max 10 seconds
      setTimeout(() => {
        clearInterval(wait);
        resolve();
      }, 10000);
    });
  }
  
  log('🎙️ Generating smart wrap-up...', 'wrap');
  
const wrapUpPrompt = `Generate a 30-40 second NATURAL CLOSING for ${displayName}'s ${timeOfDay} meditation.

**THEIR INTENTION:** ${selectedIntention?.label || 'General meditation'}

**TIME OF DAY:** ${timeOfDay}
**HOUR:** ${new Date().getHours()}

**REQUIREMENTS:**
- 3-4 exchanges between Guide and Companion
- Speak TO ${displayName}, NEVER as them
- Guide them to:
  1. Notice what shifted
  2. Deepen breath
  3. Open eyes when ready
  4. Carry this forward

**TONE - MATCH TIME OF DAY:**
${timeOfDay === 'morning' ? `
- Energizing but grounded
- "You're ready for your day"
- "Take this calm with you"
- Confident, forward-looking
` : timeOfDay === 'afternoon' ? `
- Centering, rebalancing
- "You've reset"
- "The rest of today unfolds from here"
- Present, grounded
` : `
- Softening, releasing
- "You've done enough"
- "Rest well tonight"
- Gentle, peaceful
${new Date().getHours() >= 22 ? '- EXTRA GENTLE - it\'s late, honor sleep' : ''}
`}

**NATURAL LANGUAGE - CRITICAL:**
Don't use clinical phrases. Sound like a real meditation teacher:

GOOD:
"Take a moment, ${displayName}. Notice what's different."
"You've spent this time honoring ${selectedIntention?.label}. That matters."
"When you're ready, let your eyes open."

BAD:
"Bring your awareness back to the room"
"Notice the sensations of returning"
"Integrate what you've learned"

**STRUCTURE:**
Guide: Brief acknowledgment of what they did
Companion: Validates the intention work
Guide: Breath instruction
Companion: Eye opening + forward wish

Output ONLY the closing dialogue (no quotes, no labels):`;
  try {
 const data = await SecureAPI.callAzureOpenAI([
  { 
    role: 'system', 
    content: 'Generate meditation closing. Output ONLY Guide:/Companion: dialogue. Be natural and warm.'
  },
  { role: 'user', content: wrapUpPrompt }
], {
  temperature: 0.85,
  max_tokens: 300
});

const wrapUpScript = data.choices[0].message.content.trim();
    
    log('✅ Wrap-up generated', 'success');
    
    // ✅ Play the wrap-up
    const wrapUpLines = wrapUpScript.split('\n').filter(line => line.trim());
    
    for (let i = 0; i < wrapUpLines.length; i++) {
      const line = wrapUpLines[i];
      const guideMatch = line.match(/Guide:\s*(.+)/i);
      const companionMatch = line.match(/Companion:\s*(.+)/i);
      
      if (guideMatch || companionMatch) {
        const voice = guideMatch ? 'guide' : 'therapist';
        const text = (guideMatch || companionMatch)[1].trim();
        
        await playAudio(text, voice);
        
        if (i < wrapUpLines.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 400));
        }
      }
    }
    
    log('✅ Wrap-up complete', 'complete');
    
  } catch (error) {
    log(`❌ Wrap-up generation failed: ${error.message}`, 'error');
    // Fallback to simple closing
    await playFallbackWrapUp();
  }
};

// ============================================
// TRACK LAST AMBIENT TRACK (Don't repeat)
// ============================================
const lastAmbientIndexRef = useRef(-1);

const ambientDurationRef = useRef(0);
const ambientStartTimeRef = useRef(0);

const startAmbientMusic = async () => {
  log('Starting ambient music...', 'ambient');

  let randomIndex = Math.floor(Math.random() * AMBIENT_TRACKS.length);
  
  while (randomIndex === lastAmbientIndexRef.current && AMBIENT_TRACKS.length > 1) {
    randomIndex = Math.floor(Math.random() * AMBIENT_TRACKS.length);
  }
  
  lastAmbientIndexRef.current = randomIndex;
  const ambientTrack = AMBIENT_TRACKS[randomIndex];

  if (!ambientAudioRef.current) {
    ambientAudioRef.current = new Audio();
    ambientAudioRef.current.loop = false;
    ambientAudioRef.current.preload = 'auto';
  }

  ambientAudioRef.current.src = ambientTrack;
  
  // ✅ NEW: Track when ambient loads to get duration
  ambientAudioRef.current.addEventListener('loadedmetadata', () => {
    ambientDurationRef.current = ambientAudioRef.current.duration;
    ambientStartTimeRef.current = Date.now();
    log(`🎵 Ambient duration: ${Math.round(ambientDurationRef.current)}s (${Math.floor(ambientDurationRef.current / 60)}:${String(Math.round(ambientDurationRef.current % 60)).padStart(2, '0')})`, 'ambient');
  });

  ambientAudioRef.current.src = ambientTrack;
  ambientAudioRef.current.volume = isMutedRef.current ? 0 : 0.15;
  ambientAudioRef.current.loop = false; // ✅ EXPLICIT: No loop

  if (!audioContextRef.current) {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      log('✅ AudioContext created for ambient', 'audio');
    } catch (e) {
      log(`⚠️ AudioContext creation failed: ${e.message}`, 'warn');
    }
  }

  if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
    try {
      await audioContextRef.current.resume();
      log('✅ AudioContext resumed for ambient', 'audio');
    } catch (e) {
      log(`⚠️ AudioContext resume failed: ${e.message}`, 'warn');
    }
  }

  try {
    await ambientAudioRef.current.play();
    log(`✅ Ambient playing (NO LOOP): Track ${randomIndex + 1}`, 'success');
    return true;
  } catch (err) {
    log(`❌ Ambient playback failed: ${err.message}`, 'error');
    if (err.name === 'NotAllowedError') {
      log('⚠️ Autoplay blocked - requires user interaction', 'warn');
      setCurrentStatus('Click play to start audio');
    } else {
      setCurrentStatus('Audio loading failed');
    }
    return false;
  }
};

// ============================================
// IMPROVED SESSION MANAGEMENT
// Always start fresh, save session progress
// ============================================

const saveSessionState = async () => {
  if (!currentSessionId || !firestoreService) return;
  
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    
    const sessionData = {
      scriptLines: pausedStateRef.current.scriptLines,
      currentIndex: pausedStateRef.current.currentIndex,
      ambientTime: ambientAudioRef.current?.currentTime || 0,
      timeOfDay: timeOfDay,
      isPaused: pausedStateRef.current.isPaused,
      hasStarted: hasStartedPlaying,
      selectedIntention: selectedIntention,
      currentEntry: currentEntry,
      timestamp: Date.now(),
      userName: displayName,
      sessionId: currentSessionId
    };
    
    // Save to Firestore with unique session key
    await firestoreService.updateUserData({
      [`sessions.${currentSessionId}`]: sessionData
    });
    
    log(`✅ Session saved: ${currentSessionId}`, 'session');
  } catch (err) {
    log(`⚠️ Could not save session: ${err.message}`, 'warn');
  }
};


const getIntentionGuidance = (intention) => {
  if (!intention || intention.id === 'general') return '';
  
  const guidanceMap = {
    'morning-start': `
**MORNING START APPROACH:**
- Energizing but not aggressive
- "Feel your body waking up"
- Body activation: flex hands, roll shoulders, stretch subtly
- Breath: natural then slightly deeper
- Language: awaken, ready, present, alive, begin
- Multiple body awareness passes
- Visualization: light, morning sun, fresh start
- Close with readiness for the day`,

    'energy': `
**ENERGY ACTIVATION APPROACH:**
- Active, invigorating
- "Where's the energy stuck? Let's move it."
- Strong breath work: quick exhales, engaged inhales
- Progressive activation: toes → head
- Include subtle movement cues
- Language: surge, ignite, flow, power, alive
- NO long relaxation - this is ACTIVATION
- Multiple energy-building cycles`,

    'destress': `
**STRESS RELEASE APPROACH:**
- Progressive deep relaxation
- "Your body knows it's safe here"
- Validate stress without dwelling
- Longer exhales than inhales
- Multiple body tension scans
- Language: release, soften, melt, ease, let go
- Slower pacing, more space
- Visualization: tension draining away`,

    'recharge': `
**MIDDAY RESET APPROACH:**
- Balanced restoration
- "You've done enough already"
- Light body scan (not sleep-inducing)
- Rhythmic, calming breath
- Gentle re-energizing
- Language: restore, reset, balance, refresh
- Mix settling with gentle awakening
- Ready to continue their day`,

    'stress': `
**ANXIETY GROUNDING APPROACH:**
- Nervous system regulation
- "You're safe. Right here, right now."
- 5-senses grounding (see, hear, feel, smell, taste)
- Foot/earth connection
- Breath: 4-4-4-4 mentioned naturally
- Language: safe, grounded, here, present, solid
- Multiple grounding techniques
- Body safety anchors`,

    'focus': `
**CLARITY & FOCUS APPROACH:**
- Attention training
- Anchor to single point (breath or sensation)
- Notice wandering, gently return (15-20 times)
- Gradually expand awareness while maintaining focus
- Language: clear, sharp, steady, attend, focus
- Many focus-return cycles
- Visualization: still water, clear sky
- Close with clarity for work`,

    'sleep': `
**SLEEP PREPARATION APPROACH:**
- VERY slow, gentle pacing
- "Let yourself be heavy"
- Progressive body scan: heavy, sinking, supported
- Very slow breath (5-6 seconds in/out)
- Permission to drift/sleep
- Language: rest, drift, sink, sleep, peace, heavy
- NO stimulation, NO waking at end
- Multiple relaxation passes
- Hypnagogic imagery OK
- Ultra-soft transitions`,

    'gratitude': `
**GRATITUDE CULTIVATION APPROACH:**
- Heart-opening, warm
- "You're here. That's worth appreciating."
- Multiple gratitude anchors: body, day, people, simple things
- Heart-centered awareness
- Loving-kindness elements (self-compassion first)
- Language: grateful, appreciate, warm, blessed, open, gift
- Emotional warmth appropriate
- Visualization: warm light in chest
- Close with appreciation for this moment`
  };
  
  return guidanceMap[intention.id] || '';
};
// IMPROVED SUGGESTION TOPIC PROMPT

const generateSuggestionPrompt = (userName, topic, entries, isEntryLocked) => {
  const unlockedEntries = entries.filter(entry => !isEntryLocked(entry.id));
  const recentEntries = unlockedEntries.slice(0, 3);
  
  const specificContext = recentEntries.map(e => ({
    title: e.title,
    mood: e.mood,
    snippet: e.content.substring(0, 100)
  }));

  const moods = unlockedEntries.map(e => e.mood);
  const dominantMood = moods.sort((a,b) => 
    moods.filter(v => v === a).length - moods.filter(v => v === b).length
  ).pop();

  return `Create a 75-100 second MEDITATION-STYLE conversation about "${topic.title}" for ${userName}.

THEIR ENTRIES:
${specificContext.map(e => `"${e.title}" (${e.mood}): ${e.snippet}...`).join('\n')}

Dominant Mood: ${dominantMood}

MEDITATION APPROACH:
1. Start with breath and presence: "Take a breath, ${userName}..."
2. Naturally weave in their journal experience
3. Use meditation language: "Notice...", "Allow...", "Breathe into..."
4. Keep it spacious and calm
5. VARY YOUR LANGUAGE—don't repeat the same phrases
6. 5-6 exchanges total
7. Each turn: 18-30 words MAX
8. End with: "Want to explore this more? Tap join in."

TONE EXAMPLE:
Guide: "Take a breath with me, ${userName}. You've been carrying worry about work. Feel where it sits in your body."
Companion: "Yeah, that heaviness is real. But right now, you're here. Right now, you're safe."
Guide: "What if you could soften around it, just a little? Not fix it. Just... soften."

**CRITICAL:** Use natural, varied meditation language. Don't sound scripted. Sound human and present.

Output ONLY the meditation script:
Guide: [text]
Companion: [text]
(continue)`;
};
// ========================================
// SECTION 4: IMPROVED MIC CONVERSATION PROMPT
// ========================================

// IMPROVED CLOSING MESSAGE GENERATION
const generateClosingPrompt = (userName, timeOfDay, sessionType) => {
  const period = timeOfDay === 'morning' ? 'morning' : timeOfDay === 'afternoon' ? 'afternoon' : 'evening';
  
  return `Generate a brief, warm MEDITATION-STYLE closing for ${userName} after their ${period} ${sessionType} session.

**GUIDELINES:**
- 2-3 sentences maximum
- Use breath or body awareness language naturally
- Be specific to ${period} (morning = energizing, afternoon = centering, evening = peaceful)
- Express genuine warmth with meditation tone
- VARY THE LANGUAGE—don't use the same phrases every time
- Sound like a meditation guide, not a wellness app
- NO clichés ("take care", "stay safe", "blessed day")

**MEDITATION-STYLE EXAMPLES:**
Morning: "Take one more breath with me, ${userName}. Carry this peace into your day. You're ready for what comes."
Afternoon: "Feel yourself here, ${userName}. This calm is yours to keep. The rest of today unfolds from this moment."
Evening: "Rest here a moment longer, ${userName}. You've done enough. Let yourself soften into this peace."

**AVOID (too generic):**
"Have a great day!" ❌
"Take care of yourself!" ❌  
"You've got this!" ❌

Output ONLY the closing message (no quotes, no labels).`;
};

const generateSuggestionTopics = async () => {
  const unlockedEntries = entries.filter(entry => !isEntryLocked(entry.id));
  
    if (selectedIntention && selectedIntention.id !== 'general') {
    return generateIntentionBasedSuggestions(selectedIntention, unlockedEntries);
  }
  // Image pool for suggestion cards
  const imagePool = [
    '/image/session-img52.png',
    '/image/session-img42.png',
    '/image/session-img44.png',
    '/image/session-img54.png',
    '/image/session-img31.png',
    '/image/session-img32.png',
    '/image/session-img33.png',
    '/image/session-img49.png',
    '/image/session-img53.png',
    '/image/session-img36.png',
    '/image/session-img50.png',
    '/image/session-img47.png',
    '/image/session-img39.png'
  ];
  
  // Shuffle and pick 5 random images
  const getRandomImages = () => {
    const shuffled = [...imagePool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 5);
  };
  
  if (unlockedEntries.length < 3) {
    // Return default topics if not enough unlocked entries
    const randomImages = getRandomImages();
    return [
      { title: 'Coping with Difficulty', image: randomImages[0], duration: '5 min' },
      { title: 'Importance of Journaling', image: randomImages[1], duration: '5 min' },
      { title: 'Health & Wellness', image: randomImages[2], duration: '5 min' },
      { title: 'Improve Your Journey', image: randomImages[3], duration: '5 min' },
      { title: 'Ways to Be Grateful', image: randomImages[4], duration: '5 min' }
    ];
  }

  try {
    const recentEntries = unlockedEntries.slice(0, 10);
    const entriesContext = recentEntries.map((e, idx) =>
      `Entry ${idx + 1}: "${e.title}"\nMood: ${e.mood}\nContent: ${e.content.substring(0, 100)}...`
    ).join('\n\n');

    const prompt = `Based on ${displayName}'s journal entries, suggest 5 relevant reflection topics...

**THEIR RECENT ENTRIES:**
${entriesContext}

Generate 5 topic titles (ONLY the titles, one per line, no numbering or extra text) that are:
- Personalized to their experiences
- Supportive and growth-oriented
- Varied in themes
- Short (2-6 words each)

Example format:
Finding Peace in Uncertainty
Celebrating Small Victories
Managing Work-Life Balance
Building Stronger Connections
Embracing Self-Compassion`;

 const data = await SecureAPI.callAzureOpenAI([
  { role: 'system', content: 'You are a therapeutic topic generator. Output ONLY topic titles, one per line.' },
  { role: 'user', content: prompt }
], {
  temperature: 0.9,
  max_tokens: 100
});
    const randomImages = getRandomImages();
    
    const topics = data.choices[0].message.content
      .trim()
      .split('\n')
      .filter(t => t.trim())
      .slice(0, 5)
      .map((title, idx) => ({
        title: title.trim(),
        image: randomImages[idx],
        duration: '5 min',
        isPersonalized: true
      }));

    return topics;
  } catch (error) {
    log(`Failed to generate personalized topics: ${error.message}`, 'warn');
    // Fallback to defaults
    const randomImages = getRandomImages();
    return [
      { title: 'Coping with Difficulty', image: randomImages[0], duration: '5 min' },
      { title: 'Importance of Journaling', image: randomImages[1], duration: '5 min' },
      { title: 'Health & Wellness', image: randomImages[2], duration: '5 min' },
      { title: 'Improve Your Journey', image: randomImages[3], duration: '5 min' },
      { title: 'Ways to Be Grateful', image: randomImages[4], duration: '5 min' }
    ];
  }
};
const generateIntentionBasedSuggestions = (intention, entries) => {
  const imagePool = [
    '/image/session-img53.png',
    '/image/session-img52.png',
    '/image/session-img42.png',
    '/image/session-img44.png',
    '/image/session-img54.png',
  ];
  
  const suggestionMap = {
    'morning-start': [
      { title: 'Setting Daily Intentions', image: imagePool[0], duration: '5 min' },
      { title: 'Morning Energy Boost', image: imagePool[1], duration: '5 min' },
      { title: 'Gratitude for New Day', image: imagePool[2], duration: '5 min' },
      { title: 'Mindful Morning Routine', image: imagePool[3], duration: '5 min' },
      { title: 'Start with Confidence', image: imagePool[4], duration: '5 min' },
    ],
    'energy': [
      { title: 'Energizing Breath Work', image: imagePool[0], duration: '5 min' },
      { title: 'Body Activation', image: imagePool[1], duration: '5 min' },
      { title: 'Mental Clarity Boost', image: imagePool[2], duration: '5 min' },
      { title: 'Overcome Fatigue', image: imagePool[3], duration: '5 min' },
      { title: 'Sustained Energy', image: imagePool[4], duration: '5 min' },
    ],
    'destress': [
      { title: 'Release Tension', image: imagePool[0], duration: '5 min' },
      { title: 'Calm Your Mind', image: imagePool[1], duration: '5 min' },
      { title: 'Stress Relief Breathing', image: imagePool[2], duration: '5 min' },
      { title: 'Let Go of Worry', image: imagePool[3], duration: '5 min' },
      { title: 'Find Your Center', image: imagePool[4], duration: '5 min' },
    ],
    'recharge': [
      { title: 'Restore Your Energy', image: imagePool[0], duration: '5 min' },
      { title: 'Midday Reset', image: imagePool[1], duration: '5 min' },
      { title: 'Recharge & Renew', image: imagePool[2], duration: '5 min' },
      { title: 'Quick Energy Restore', image: imagePool[3], duration: '5 min' },
      { title: 'Balance & Recharge', image: imagePool[4], duration: '5 min' },
    ],
    'stress': [
      { title: 'Stress Release Meditation', image: imagePool[0], duration: '5 min' },
      { title: 'Calm the Nervous System', image: imagePool[1], duration: '5 min' },
      { title: 'Grounding When Anxious', image: imagePool[2], duration: '5 min' },
      { title: 'Progressive Relaxation', image: imagePool[3], duration: '5 min' },
      { title: 'Peace in Chaos', image: imagePool[4], duration: '5 min' },
    ],
    'focus': [
      { title: 'Sharpen Your Focus', image: imagePool[0], duration: '5 min' },
      { title: 'Clear Mental Clutter', image: imagePool[1], duration: '5 min' },
      { title: 'Concentration Practice', image: imagePool[2], duration: '5 min' },
      { title: 'Mindful Work Session', image: imagePool[3], duration: '5 min' },
      { title: 'Attention Training', image: imagePool[4], duration: '5 min' },
    ],
    'sleep': [
      { title: 'Prepare for Deep Sleep', image: imagePool[0], duration: '5 min' },
      { title: 'Body Scan for Sleep', image: imagePool[1], duration: '5 min' },
      { title: 'Release Day\'s Tension', image: imagePool[2], duration: '5 min' },
      { title: 'Quiet the Mind', image: imagePool[3], duration: '5 min' },
      { title: 'Drift into Rest', image: imagePool[4], duration: '5 min' },
    ],
    'gratitude': [
      { title: 'Gratitude Practice', image: imagePool[0], duration: '5 min' },
      { title: 'Appreciate This Moment', image: imagePool[1], duration: '5 min' },
      { title: 'Counting Your Blessings', image: imagePool[2], duration: '5 min' },
      { title: 'Thankful Heart Meditation', image: imagePool[3], duration: '5 min' },
      { title: 'Joy in Simple Things', image: imagePool[4], duration: '5 min' },
    ],
  };
  
  return suggestionMap[intention.id] || suggestionMap['stress'];
};
const pausedStateRef = useRef({
  scriptLines: [],
  currentIndex: 0,
  isPaused: false
});
const audioContextRef = useRef(null);

// ✅ ADD THIS BLOCK HERE
// ✅ Track what opening techniques we've used recently
const recentOpeningsRef = useRef([]);

const addRecentOpening = (technique) => {
  recentOpeningsRef.current.push(technique);
  // Keep only last 5 sessions in memory
  if (recentOpeningsRef.current.length > 5) {
    recentOpeningsRef.current.shift();
  }
  log(`📝 Tracked opening: ${technique} (recent: ${recentOpeningsRef.current.join(', ')})`, 'opening');
};

const getOpeningTechnique = (userIntention) => {
  // Avoid repeating same opening
  const usedTechniques = new Set(recentOpeningsRef.current);
  
  const openingOptions = [
    'grounding', 'breath_awareness', 'body_scan_light',
    'comfort_space', 'sensory', 'visualization_light',
    'heartbeat', 'intention_setting'
  ];
  
  // Get available techniques (not used recently)
  const available = openingOptions.filter(t => !usedTechniques.has(t));
  
  // If all used, reset and pick from all
  const technique = available.length > 0 
    ? available[Math.floor(Math.random() * available.length)]
    : openingOptions[Math.floor(Math.random() * openingOptions.length)];
  
  addRecentOpening(technique);
  log(`✅ Selected opening technique: ${technique}`, 'opening');
  return technique;
};
// ✅ END ADD BLOCK
  // Keep refs in sync with state
  useEffect(() => {
    isMutedRef.current = isMuted;
    if (ambientAudioRef.current) {
  ambientAudioRef.current.volume = isMuted ? 0 : 0.18;  // ← Match your new volume
    }
    if (currentAudioRef.current) {
      currentAudioRef.current.volume = isMuted ? 0 : 1;
    }
  }, [isMuted]);

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
    if (currentAudioRef.current) {
      currentAudioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  // Debug logger
  const log = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${type.toUpperCase()}: ${message}`;
    console.log(logEntry);
    setDebugLog(prev => [...prev.slice(-20), logEntry]);
  }, []);

  // Determine time of day
  useEffect(() => {
    const hour = new Date().getHours();
    let period = '';
    let backgrounds = [];

    if (hour >= 5 && hour < 12) {
      period = 'morning';
      backgrounds = REFLECTION_BACKGROUNDS.morning;
    } else if (hour >= 12 && hour < 18) {
      period = 'afternoon';
      backgrounds = REFLECTION_BACKGROUNDS.afternoon;
    } else {
      period = 'evening';
      backgrounds = REFLECTION_BACKGROUNDS.evening;
    }

    setTimeOfDay(period);
    setBackgroundImage(backgrounds[Math.floor(Math.random() * backgrounds.length)]);
    log(`Time of day set to: ${period}`);
  }, [log]);

  // Create ambient audio element on mount
useEffect(() => {
  ambientAudioRef.current = new Audio();
  ambientAudioRef.current.loop = true;
  ambientAudioRef.current.volume = 0.3;
  log('Ambient audio element created');

  // ✅ Test if audio can load
  const testAudio = async () => {
    for (let i = 0; i < AMBIENT_TRACKS.length; i++) {
      try {
        const response = await fetch(AMBIENT_TRACKS[i], { method: 'HEAD' });
        if (response.ok) {
          log(`✅ Audio file ${i + 1} accessible`, 'init');
        } else {
          log(`❌ Audio file ${i + 1} not found (${response.status})`, 'error');
        }
      } catch (err) {
        log(`❌ Audio file ${i + 1} fetch error: ${err.message}`, 'error');
      }
    }
  };
  
  testAudio();

  return () => {
    stopReflection();
    if (ambientAudioRef.current) {
      ambientAudioRef.current.pause();
      ambientAudioRef.current = null;
    }
    audioQueueRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
    audioQueueRef.current.clear();
  };
}, []);

// NEW FUNCTION: Start inactivity timer
const startInactivityTimer = () => {
  if (inactivityTimerRef.current) {
    clearTimeout(inactivityTimerRef.current);
  }
  
  // ✅ 45 SECONDS of inactivity before suggesting wrap-up
  // This allows for natural conversation pauses
  inactivityTimerRef.current = setTimeout(async () => {
    if (isMicActive && !isHostSpeakingRef.current) {
      log('⏰ Long silence detected', 'timer');
      
      const ambientEnded = ambientAudioRef.current && ambientAudioRef.current.ended;
      
      if (ambientEnded) {
        // Only auto-close if ambient music actually ended
        log('Ambient ended, suggesting wrap-up', 'end');
        await giveClosingMessage();
      } else if (ambientAudioRef.current && !ambientAudioRef.current.paused) {
        // Ambient still playing - offer a gentle prompt but keep conversation open
        log('Offering gentle prompt to continue', 'timer');
        
        const promptVariations = [
          `${displayName}, I'm still here when you're ready.`,
          `Take your time. I'm listening.`,
          `What comes next for you?`,
          `What are you noticing right now?`,
          `Anything else you'd like to explore?`
        ];
        
        const prompt = promptVariations[Math.floor(Math.random() * promptVariations.length)];
        isHostSpeakingRef.current = true;
        await speakChunk(prompt, 'guide');
        isHostSpeakingRef.current = false;
        
        // Restart timer for another 45 seconds
        startInactivityTimer();
      }
    }
  }, 45000); // 45 seconds - allows for thinking time
};


const giveClosingMessage = async () => {
  log('👋 Giving closing message', 'closing');
  setMicStatus('');
  
  try {
    const prompt = generateClosingPrompt(displayName, timeOfDay, isMicActive ? 'conversation' : 'reflection');

    const closingMessage = await generateContent(
      prompt,
      'You are a warm, therapeutic guide. Output only the closing message, no formatting.'
    );

    isHostSpeakingRef.current = true;
    await playAudio(closingMessage.trim(), 'guide');
    isHostSpeakingRef.current = false;
  } catch (error) {
    log(`Error generating closing: ${error.message}`, 'error');
    // Fallback
    const fallback = `Thank you for this time together, ${userName}. Wishing you peace.`;
    isHostSpeakingRef.current = true;
    await playAudio(fallback, 'guide');
    isHostSpeakingRef.current = false;
  }
};

// 4. FIX initializeSpeechRecognition (from quiz - accumulate properly):

const initializeSpeechRecognition = async () => {
  try {
    if (typeof window.SpeechSDK === 'undefined') {
      throw new Error('Azure Speech SDK not loaded');
    }

    const SpeechSDK = window.SpeechSDK;
    
    // ✅ GET TOKEN FROM SECURE API (no keys exposed!)
    log('🔐 Getting secure speech token...', 'mic');
    const { token, region } = await SecureAPI.getAzureSpeechToken();
    
    // ✅ USE TOKEN INSTEAD OF API KEY
    const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
    
    speechConfig.speechRecognitionLanguage = "en-US";
    speechConfig.setProfanity(SpeechSDK.ProfanityOption.Masked);

    const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
    const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

    azureRecognizerRef.current = recognizer;

    // ✅ TOKEN REFRESH LOGIC
    // Azure tokens expire after 10 minutes - refresh before that
    const refreshInterval = setInterval(async () => {
      if (isMicActiveRef.current) {
        try {
          log('🔄 Refreshing speech token...', 'mic');
          const { token: newToken } = await SecureAPI.getAzureSpeechToken();
          recognizer.authorizationToken = newToken;
          log('✅ Token refreshed', 'mic');
        } catch (err) {
          log('⚠️ Token refresh failed', 'warn');
        }
      } else {
        clearInterval(refreshInterval);
      }
    }, 540000); // Refresh every 9 minutes

    // ✅ RECOGNIZING (interim results while speaking)
    recognizer.recognizing = (s, e) => {
      if (!isMicActiveRef.current) return;

      if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
        const interimText = e.result.text;
        setUserTranscript(interimText);
        log(`🎤 Interim: ${interimText}`, 'mic');
        lastUserActivityRef.current = Date.now();
      }
    };

    // ✅ RECOGNIZED (final result)
    recognizer.recognized = (s, e) => {
      if (!isMicActiveRef.current) return;

      if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
        const text = e.result.text;

        if (text && text.trim().length > 0) {
          log(`✅ Final: "${text}"`, 'mic');
          accumulatedTranscriptRef.current += text + ' ';
          lastUserActivityRef.current = Date.now();

          // Clear old silence timer
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
          }

          // Wait 2 seconds for more speech
          silenceTimerRef.current = setTimeout(() => {
            if (accumulatedTranscriptRef.current.trim().length > 3 && isMicActiveRef.current) {
              log(`📤 Sending: "${accumulatedTranscriptRef.current.trim()}"`, 'send');
              handleUserQuestion(accumulatedTranscriptRef.current.trim());
              accumulatedTranscriptRef.current = '';
              setUserTranscript('');
            }
          }, 2000);
        }
      } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
        log('⚠️ No speech detected', 'warn');
      }
    };

    // ✅ CANCELED (errors)
    recognizer.canceled = (s, e) => {
      log(`❌ Canceled: ${e.reason}`, 'error');

      if (e.reason === SpeechSDK.CancellationReason.Error) {
        log(`Error details: ${e.errorDetails}`, 'error');
      }

      // ✅ AUTO-RESTART on error
      if (isMicActiveRef.current) {
        log('🔄 Auto-restarting after cancellation...', 'mic');
        setTimeout(() => {
          if (isMicActiveRef.current && azureRecognizerRef.current) {
            try {
              azureRecognizerRef.current.startContinuousRecognitionAsync();
              log('✅ Restarted Azure recognizer', 'mic');
            } catch (err) {
              log(`Restart error: ${err.message}`, 'error');
            }
          }
        }, 500);
      }
    };

    // ✅ SESSION STOPPED (when continuous recognition ends)
    recognizer.sessionStopped = (s, e) => {
      log('🛑 Azure session stopped', 'mic');

      // Send any remaining text
      if (accumulatedTranscriptRef.current.trim().length > 3 && isMicActiveRef.current) {
        log(`📤 Final send on session stop: "${accumulatedTranscriptRef.current.trim()}"`, 'send');
        handleUserQuestion(accumulatedTranscriptRef.current.trim());
        accumulatedTranscriptRef.current = '';
      }

      // ✅ AUTO-RESTART if still active
      if (isMicActiveRef.current) {
        log('🔄 Restarting Azure recognizer after session stop...', 'mic');
        setTimeout(() => {
          if (isMicActiveRef.current && azureRecognizerRef.current) {
            try {
              azureRecognizerRef.current.startContinuousRecognitionAsync();
              log('✅ Restarted Azure recognizer after session stop', 'mic');
            } catch (err) {
              log(`Restart error: ${err.message}`, 'error');
            }
          }
        }, 300);
      }
    };

    log('✅ Azure Speech Recognizer initialized with secure token', 'mic');
    return recognizer;

  } catch (error) {
    log(`❌ Azure initialization error: ${error.message}`, 'error');
    throw error;
  }
};
const toggleMicrophone = async () => {
  if (!isMicActive) {
    // ======================================== 
    // USER JOINING CONVERSATION
    // ========================================
    log('🎤 User joining conversation', 'mic');

    // ✅ FULL STATE RESET
    isMicActiveRef.current = true;
    accumulatedTranscriptRef.current = '';
    setTranscript([]);
    setUserTranscript('');
    setLoadingMessage('');

    // Stop any current audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }

    // ✅ PAUSE reflection (don't stop it)
    const wasPlayingReflection = isPlaying && !pausedStateRef.current.isPaused;
    if (wasPlayingReflection) {
      pausedStateRef.current.isPaused = true;
      setIsPaused(true);
      log('✅ Reflection paused for mic', 'pause');
    }

    // ✅ Keep ambient playing (don't restart)
    if (!ambientAudioRef.current || ambientAudioRef.current.paused) {
      await startAmbientMusic();
    }

    setIsMicActive(true);
    setMicStatus('listening');
    setIsListening(true);
    lastUserActivityRef.current = Date.now();

    try {
      let recognizer = azureRecognizerRef.current;
      
      if (!recognizer) {
        log('📱 Initializing Azure Speech Recognizer...', 'mic');
        recognizer = await initializeSpeechRecognition();
      }

      log('🎙️ Starting continuous recognition...', 'mic');
      
      recognizer.startContinuousRecognitionAsync(
        () => {
          log('✅ Azure continuous recognition started', 'mic');
          setMicStatus('listening');
        },
        (error) => {
          log(`❌ Failed to start mic: ${error}`, 'error');
          setIsMicActive(false);
          isMicActiveRef.current = false;
          alert(`Microphone error: ${error}`);
        }
      );

    } catch (error) {
      log(`❌ Mic initialization error: ${error.message}`, 'error');
      alert(`Failed to start microphone: ${error.message}`);
      setIsMicActive(false);
      isMicActiveRef.current = false;
      return;
    }

// ✅ Greeting happens BEFORE startRecognition in selectInteractionMode
    // So this section just waits and confirms mic is ready
    log('✅ Microphone ready for user input', 'mic');
    
    // Don't repeat greeting - it's handled in selectInteractionMode
    await new Promise(resolve => setTimeout(resolve, 500));
    setMicStatus('listening');
  } else {
    // ========================================
    // USER LEAVING CONVERSATION
    // ========================================
    log('🎤 User turning off mic', 'mic');

    isMicActiveRef.current = false;
    setIsMicActive(false);

    // Send any remaining text
    if (accumulatedTranscriptRef.current.trim().length > 3) {
      log(`📤 Final send: "${accumulatedTranscriptRef.current.trim()}"`, 'send');
      await handleUserQuestion(accumulatedTranscriptRef.current.trim());
      accumulatedTranscriptRef.current = '';
    }

    setIsListening(false);
    setMicStatus('');
    setUserTranscript('');
    setTranscript([]);

    // ✅ Safe stop recognition
    safeStopRecognition(azureRecognizerRef);

    // Clear timers
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    // ✅ FIX 2: Resume reflection if it was paused
    const ambientEnded = ambientAudioRef.current && ambientAudioRef.current.ended;

    if (ambientEnded) {
      // If ambient ended during conversation, give closing
      await giveClosingMessage();
    } else if (pausedStateRef.current.isPaused && pausedStateRef.current.scriptLines.length > 0) {
      // ✅ RESUME the paused reflection
      log('▶️ Resuming paused reflection after user leaves mic', 'resume');
      await resumeReflection();
    } else {
      // Nothing to resume, show farewell
      setTimeout(() => {
        if (!isPlaying && !isSuggestionMode) {
          showFarewellCard();
        }
      }, 1000);
    }
  }
};



useEffect(() => {
  return () => {
    // ✅ FULL CLEANUP WITH SAFE HELPERS
    if (azureRecognizerRef.current) {
      try {
        safeStopRecognition(azureRecognizerRef);
        azureRecognizerRef.current.close();
      } catch (err) {
        console.log('Cleanup error:', err);
      }
    }

    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    isMicActiveRef.current = false;
    accumulatedTranscriptRef.current = '';
  };
}, []);

const ensureAmbientPlaying = async () => {
  if (!ambientAudioRef.current) return;
  
  // ✅ If ambient ended, restart it
  if (ambientAudioRef.current.ended) {
    log('🔄 Ambient ended, restarting...', 'ambient');
    await startAmbientMusic();
  }
  
  // ✅ If ambient paused unexpectedly, resume it
  if (ambientAudioRef.current.paused && (isPlaying || isMicActive)) {
    log('🔄 Ambient paused unexpectedly, resuming...', 'ambient');
    try {
      await ambientAudioRef.current.play();
    } catch (err) {
      log(`⚠️ Could not resume ambient: ${err.message}`, 'warn');
    }
  }
};

// ✅ ADD this useEffect to monitor ambient status:

useEffect(() => {
  // Check ambient every 2 seconds when playing
  const interval = setInterval(() => {
    if (isPlaying || isMicActive) {
      ensureAmbientPlaying();
    }
  }, 2000);

  return () => clearInterval(interval);
}, [isPlaying, isMicActive]);

const resumeReflectionNaturally = async () => {
  log('▶️ Resuming reflection naturally after conversation', 'resume');
  
  setMicStatus('');
  setTranscript([]);
  
  setTimeout(async () => {
    const { scriptLines, currentIndex } = pausedStateRef.current;
    
    if (currentIndex >= scriptLines.length) {
      log('Reflection was complete, giving closing', 'info');
      await giveClosingMessage();
      return;
    }
    
    // Extract the topic that was being discussed
    const lastLine = scriptLines[currentIndex - 1] || scriptLines[currentIndex];
    const topicMatch = lastLine.match(/(?:Guide|Companion):\s*(.+)/i);
    const lastTopic = topicMatch ? topicMatch[1].trim() : '';
    lastReflectionTopicRef.current = lastTopic;
    
    // Check if user's question was related to what was being discussed
    const userQuestion = userQuestionTopicRef.current.toLowerCase();
    const wasRelated = lastTopic.toLowerCase().split(' ').some(word => 
      word.length > 4 && userQuestion.includes(word)
    );
    
    let transitionText = '';
    
    if (wasRelated) {
      // User asked about related topic - acknowledge and move forward naturally
      transitionText = "So, having said that, I'm glad we could explore that together. Moving forward, ";
    } else {
      // User asked about something different - acknowledge and return to original topic
      transitionText = "That's wonderful that we could discuss that. Now, continuing from before, ";
    }
    
    // Get next content to continue with
    let nextContent = '';
    if (currentIndex < scriptLines.length) {
      const nextLine = scriptLines[currentIndex];
      const contentMatch = nextLine.match(/(?:Guide|Companion):\s*(.+)/i);
      if (contentMatch) {
        nextContent = contentMatch[1].trim();
        // Make it sound natural - don't just repeat exactly
        nextContent = nextContent.replace(/^(So|Now|And|But),?\s*/i, '');
      }
    }
    
    const fullTransition = transitionText + nextContent;
    
    // Speak the transition
    isHostSpeakingRef.current = true;
    await playAudio(fullTransition, 'guide');
    isHostSpeakingRef.current = false;
    
    // Continue with rest of script
    pausedStateRef.current.isPaused = false;
    setIsPaused(false);
    setIsPlaying(true);
    
    const scriptText = scriptLines.join('\n');
    await playScript(scriptText, currentIndex + 1);
  }, 800);
};
const handleSuggestionClick = (topic) => {
  setSelectedSuggestion(topic);
  setShowSuggestionConfirm(true);
};

const cancelSuggestion = () => {
  log('❌ User cancelled suggestion', 'action');
  setSelectedSuggestion(null);
  setShowSuggestionConfirm(false); // ✅ Make sure modal closes
};

const confirmSuggestion = async () => {
  log(`✅ User confirmed suggestion: ${selectedSuggestion.title}`, 'action');
  
  // ✅ Close confirmation modal immediately
  setShowSuggestionConfirm(false);
  
  // ✅ Set suggestion mode
  setIsSuggestionMode(true);
  setIsShowingFarewell(false);

  // ✅ Reset state
  setTranscript([]);
  setLoadingMessage('');
  setCurrentStatus('');
  setIsPaused(false);
  pausedStateRef.current = { scriptLines: [], currentIndex: 0, isPaused: false };

  // Stop any current playback
  if (currentAudioRef.current) {
    currentAudioRef.current.pause();
    currentAudioRef.current = null;
  }

  // Turn off mic if active
  if (isMicActive) {
    await toggleMicrophone();
  }

  setIsPlaying(true);

  // ✅ Start or continue ambient
  if (!ambientAudioRef.current || ambientAudioRef.current.paused || ambientAudioRef.current.ended) {
    log('🎵 Starting ambient for suggestion', 'ambient');
    await startAmbientMusic();
  } else {
    log('✅ Ambient already playing, continuing', 'ambient');
  }

  // Play the suggestion
  await playSuggestionTopic(selectedSuggestion);
};


const playSuggestionTopic = async (topic) => {
  log(`🎯 Starting suggestion topic: ${topic.title}`, 'suggestion');

  setTranscript([]);
  setLoadingMessage('');
  setCurrentStatus('');
  setIsPaused(false);
  pausedStateRef.current = { scriptLines: [], currentIndex: 0, isPaused: false };

  if (currentAudioRef.current) {
    currentAudioRef.current.pause();
    currentAudioRef.current = null;
  }

  if (ambientAudioRef.current) {
    ambientAudioRef.current.pause();
    ambientAudioRef.current.currentTime = 0;
    ambientAudioRef.current = null;
  }

  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  abortControllerRef.current = new AbortController();

  try {
    log('🎵 Starting NEW ambient for suggestion topic', 'ambient');
    await startAmbientMusic();

    setLoadingMessage(`Preparing ${topic.title}...`);

    const unlockedEntries = entries.filter(entry => !isEntryLocked(entry.id));

    const prompt = generateSuggestionPromptWithInvitation(displayName, topic, entries, isEntryLocked);
const script = await generateContent(
  prompt, 
  `You are a master meditation guide creating deeply personal sessions.

**CRITICAL LENGTH REQUIREMENT:** 
Generate 120-150+ exchanges. Count them as you go.
This meditation needs to fill 8-10 minutes of speaking time.
MORE EXCHANGES = BETTER. Don't stop at 60-80, keep going to 120-150+.

**FOCUS ENTIRELY ON USER'S INTENTION:**
The user chose: "${selectedIntention?.label}"
EVERY EXCHANGE must relate to this intention.
This is what they want to work on - honor that choice throughout.

**STRUCTURE:**
- Opening & settling: 5-8 exchanges
- Body awareness: 15-20 exchanges  
- Breath work: 15-20 exchanges
- Deep intention work: 40-60 exchanges (THIS IS THE CORE)
- Integration: 20-30 exchanges
- Continue naturally: remaining exchanges

**STYLE:**
- Natural, warm, genuine dialogue between Guide and Companion
- They speak TO the user, never AS the user
- Guide eyes closed early in session
- Vary your language - don't repeat the same phrases
- Build gradually with multiple approaches
- Keep relating back to: ${selectedIntention?.label}

**DO NOT:**
- Include a closing section (system generates this automatically)
- Write user responses
- Stop before 120+ exchanges
- Lose focus on the chosen intention

GENERATE THE FULL 120-150+ EXCHANGE MEDITATION NOW:`
);

    log(`✅ Script generated for ${topic.title}`, 'success');

    const lines = script.split('\n').filter(line => line.trim());
    log(`✅ Script ready with invitation`, 'success');
    setLoadingMessage('');
    setCurrentStatus('Now playing...');

    // ✅ Play the script
    await playScript(script);

    log('✅ Suggestion topic complete', 'complete');

    setCurrentStatus('Complete');
    
    // ✅ FIX: After suggestion finishes, show farewell and close modal
    setTimeout(() => {
      setShowSuggestionConfirm(false); // ✅ Close confirmation modal
      setSelectedSuggestion(null); // ✅ Clear selection
      setIsSuggestionMode(false); // ✅ Exit suggestion mode
      showFarewellCard(); // ✅ Show farewell with new suggestions
    }, 1500);

  } catch (error) {
    log(`❌ Suggestion topic error: ${error.message}`, 'error');
    setLoadingMessage('');
    
    // ✅ On error, also close and show farewell
    setShowSuggestionConfirm(false);
    setSelectedSuggestion(null);
    setIsSuggestionMode(false);
    showFarewellCard();
  }
};

const generateSuggestionPromptWithInvitation = (displayName, topic, entries, isEntryLocked) => {
  const unlockedEntries = entries.filter(entry => !isEntryLocked(entry.id));
  const recentEntries = unlockedEntries.slice(0, 3);
  
  const specificContext = recentEntries.map(e => ({
    title: e.title,
    mood: e.mood,
    snippet: e.content.substring(0, 100)
  }));

  const moods = unlockedEntries.map(e => e.mood);
  const dominantMood = moods.sort((a,b) => 
    moods.filter(v => v === a).length - moods.filter(v => v === b).length
  ).pop();

  return `Create a 90-120 second MEDITATION CONVERSATION about "${topic.title}" for ${displayName}.

**THEIR RECENT ENTRIES:**
${specificContext.map(e => `"${e.title}" (${e.mood}): ${e.snippet}...`).join('\n')}

Dominant Mood: ${dominantMood}

**STRUCTURE - IMPORTANT:**
1. **Hosts explore the topic naturally** (60-80 seconds):
   - Guide: Opens the topic related to "${topic.title}"
   - Companion: Deepens or adds perspective
   - Guide/Companion: Continue exploring
   - Weave in their journal themes naturally
   - Use meditation language: "Notice...", "What if...", "Feel into..."

2. **NATURAL INVITATION (20-30 seconds):**
   - Guide: "This is your safe space, ${displayName}. Want to explore this together?"
   - Companion: "We're here to listen and support. Voice or text, whatever feels right."
   - Guide: "No pressure - we'll keep going either way, and you can join anytime."

3. **PAUSE FOR POTENTIAL RESPONSE:**
   - Wait 4-5 seconds for user to potentially join
   - If no response, continue naturally

4. **CONTINUE:**
   - Companion: Continue with the topic gently
   - Guide: Deepen or wrap naturally

**TONE:**
- Warm, spacious, non-demanding
- Inviting but not pushy
- Meditation-style language throughout
- Sound like real guides having a conversation

**TOTAL EXCHANGES:** 6-8 turns
**EACH TURN:** 20-35 words MAX

**EXAMPLE STRUCTURE:**
Guide: "You've been carrying something about work lately. What if that heaviness could teach you something?"
Companion: "Yeah, sometimes our struggles are doorways. Right now, notice where you feel that in your body."
Guide: "This is your space, ${displayName}. Want to talk about what's coming up? Voice or text, you choose."
Companion: "We're listening either way. And if you want to stay quiet, that's perfect too."
Guide: "Let's pause here. You can join us anytime..."
[5 second pause]
Guide: "Continuing on, what would it feel like to soften around this?"

Output ONLY the meditation script dialogue:
Guide: [text]
Companion: [text]
(continue with invitation in the middle)`;
};

const generateMicConversationPrompt = (displayName, entries, conversationHistory, lastReflectionTopic, isEntryLocked, userQuestion) => {
  const unlockedEntries = entries.filter(entry => !isEntryLocked(entry.id));
  const recentEntries = unlockedEntries.slice(0, 3);
  
  const entriesContext = recentEntries.map((e, idx) => 
    `Entry ${idx + 1}: "${e.title}"\n${e.content.substring(0, 100)}...\nMood: ${e.mood}`
  ).join('\n\n');

  return `You're a warm, insightful meditation guide having a real conversation with ${displayName} in their safe space.

**USER JUST ASKED:** "${userQuestion}"

**THEIR JOURNAL (Recent Context):**
${entriesContext}

**CONVERSATION SO FAR:**
${conversationHistory.slice(-2).map(msg => `${msg.role === 'user' ? 'User' : 'Guide'}: ${msg.content}`).join('\n')}

**YOUR ROLE - IMPORTANT:**
This is NOT a quick "yes/no" answer. This is a REAL conversation where you help ${displayName} get INSIGHTS and BECOME BETTER.
You know their journal, their patterns, their struggles. Reference them naturally.

✅ DO THIS:
1. **Listen deeply** to what they're really asking
2. **Show you know them** - reference something from their entries naturally
3. **Ask a reflection question** that helps THEM find their own answer
   - "When did you first notice that pattern?"
   - "How does your body feel when you think about that?"
   - "If you could change one thing about this situation, what would it be?"
   - "What would it feel like if you were already okay with this?"
4. **Validate their experience** - "That's real, and you're right to notice it"
5. **Help them gain insight** - not just answer, but guide them to understanding

❌ DON'T DO THIS:
- Generic answers ("You've got this!")
- Therapy answers (don't diagnose)
- Repeating what they said back to them
- Turning it back to meditation immediately
- Speaking for them
- Using the same response pattern every time
- Ignoring their journal and who they are

**PERSONALIZATION - KEY:**
- Know their mood from entries (they said they've been feeling ${recentEntries[0]?.mood})
- Know their challenges (they wrote about: ${recentEntries.map(e => e.title).join(', ')})
- Reference specific details they shared
- Match their tone and language
- Be genuinely curious about their situation

**TONE:**
- Curious and warm - like a trusted friend asking right questions
- Grounded in meditation practice (use breath/body language naturally)
- Real human connection, not clinical
- Specific to THEM, not generic
- Acknowledge difficulty without minimizing

**LENGTH:** 2-3 sentences MAX (35-55 words) that include:
- Acknowledgment that shows you understand their situation
- ONE reflection question OR insight that's personalized to them
- Optional: subtle connection to meditation practice

**EXAMPLES (Personalized):**

User: "How do I stop worrying so much?"
Their journal: "Work stress, feeling overwhelmed"
Guide: "I see you've been carrying work stress - that heaviness is real. When you notice that worry starting, what comes first - the thought or the feeling in your body? Sometimes if we catch it at the feeling level, we have more choice."

User: "I don't know how to handle my anger"
Their journal: "Struggles with patience, reactive"
Guide: "You wrote about feeling reactive lately. Anger is important information - it tells us what matters to us. Right now, in your body, where do you feel that anger? What's it protecting?"

User: "How can I be more confident?"
Their journal: "Self-doubt, perfectionism"
Guide: "You mentioned the perfectionism pattern. What if confidence isn't about being perfect, but about showing up even when you're uncertain? What's one small thing you could do today that scares you a little?"

Return ONLY your response as plain text (no labels, no formatting).`;
};
const handleUserQuestion = async (text) => {
  // ✅ GUARD: Don't process if not really in interaction mode
  if (!interactionMode) {
    log('⚠️ Received question but not in interaction mode', 'warn');
    return;
  }
  
  if (!text.trim() || isProcessingQuestion) return;
  
  setIsProcessingQuestion(true);
  setMicStatus('thinking');
  setIsListening(false);
  setUserTranscript('');
  lastUserActivityRef.current = Date.now();
  
  log(`💬 Processing user question: ${text}`, 'mic');

  safeStopRecognition(azureRecognizerRef);
  
  if (inactivityTimerRef.current) {
    clearTimeout(inactivityTimerRef.current);
  }

  conversationHistoryRef.current.push({
    role: 'user',
    content: text
  });

  userQuestionTopicRef.current = text;

  try {
    const unlockedEntries = entries.filter(entry => !isEntryLocked(entry.id));
    const recentEntries = unlockedEntries.slice(0, 3);

    // ✅ USE THE SMART CONVERSATION PROMPT
    const systemPrompt = generateMicConversationPrompt(
      displayName, 
      entries, 
      conversationHistoryRef.current, 
      lastReflectionTopicRef.current, 
      isEntryLocked, 
      text
    );

const data = await SecureAPI.callAzureOpenAI([
  { role: 'system', content: systemPrompt },
  ...conversationHistoryRef.current.slice(-4)
], {
  temperature: 0.85,
  max_tokens: 150
});

const aiResponse = data.choices[0].message.content.trim();
    
    conversationHistoryRef.current.push({
      role: 'assistant',
      content: aiResponse
    });
log(`🤖 AI Response: ${aiResponse}`, 'ai');

    setTranscript(prev => [...prev, {
      voice: 'guide',
      text: aiResponse,
      timestamp: Date.now()
    }]);

    setMicStatus('');
    
    // ✅ ALWAYS speak the response - whether voice or text mode
    const chunks = chunkTextForSpeech(aiResponse, 200);
    
    isHostSpeakingRef.current = true;
    
    for (const chunk of chunks) {
      if (pausedStateRef.current.isPaused) {
        log('⏸️ Paused during host response', 'pause');
        break;
      }
      // ✅ Use speakChunk which generates audio even in text mode
      await speakChunk(chunk, 'guide');
      if (chunks.length > 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    isHostSpeakingRef.current = false;
    log('✅ Finished speaking response (audio + text)', 'complete');

    // ✅ DO NOT AUTO-END - Keep listening
    // Conversation continues naturally
    await new Promise(resolve => setTimeout(resolve, 500));

  } catch (error) {
    log(`❌ Question handling error: ${error.message}`, 'error');
    setTranscript(prev => [...prev, {
      voice: 'system',
      text: `Let me listen to that again.`,
      timestamp: Date.now()
    }]);
} finally {
    setIsProcessingQuestion(false);
    setMicStatus('listening');
    setIsListening(true);
    
    // ✅ KEEP MIC ACTIVE AFTER RESPONSE
    if (isMicActive && azureRecognizerRef.current) {
      try {
        safeStartRecognition(azureRecognizerRef);
        log('🎤 Mic restarted, ready for next question', 'mic');
      } catch (e) {
        log('Mic restart error', 'warn');
      }
    }
    
    // ✅ RESTART INACTIVITY TIMER (45 seconds of silence before gentle prompt)
    startInactivityTimer();
    
    // ✅ Clear old transcript messages to show latest
    // Don't clear - keep conversation history visible
  }
};
const showFarewellCard = () => {
  log('👋 Showing farewell card', 'farewell');
  
  setIsShowingFarewell(true);
  setIsPlaying(false);
  setIsSuggestionMode(false);
  setTranscript([]);
  setLoadingMessage('');
  
  // ✅ Keep ambient playing but fade it down
  if (ambientAudioRef.current && !ambientAudioRef.current.paused) {
    fadeOutAmbient();
  }
  
  // ✅ Reload suggestions for new farewell card
  generateSuggestionTopics();
};
const closeFarewell = () => {
  setIsShowingFarewell(false);
  onClose();
};
  /**
   * PRE-LOAD AUDIO with caching
   */
const preloadAudio = async (text, voice) => {
  const cacheKey = `${voice}-${text}`;

  if (isPreloadingRef.current.has(cacheKey) || audioQueueRef.current.has(cacheKey)) {
    log(`Cache hit or already preloading: ${voice}`, 'cache');
    return true;
  }

  isPreloadingRef.current.add(cacheKey);
  log(`🔄 Preloading ${voice}: ${text.substring(0, 30)}...`, 'preload');

  try {
    // ✅ 400ms delay between requests (increased for safety)
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // ✅ USE SECURE API - NO MORE EXPOSED KEY
    const audioBlob = await SecureAPI.callHumeTTSSecure(
      text,
      VOICE_CONFIG[voice],
      0.98
    );

    const url = URL.createObjectURL(audioBlob);

    audioQueueRef.current.set(cacheKey, { url, text, voice, used: false });
    log(`✅ Preloaded: ${voice} (${text.substring(0, 30)}...)`, 'success');
    return true;
  } catch (error) {
    log(`❌ Preload error: ${error.message}`, 'error');
    return false;
  } finally {
    isPreloadingRef.current.delete(cacheKey);
  }
};

const preloadNextLine = async (lines, currentIndex) => {
  // ✅ Only preload THE NEXT line
  if (currentIndex >= lines.length) return;
  
  const line = lines[currentIndex];
  const guideMatch = line.match(/Guide:\s*(.+)/i);
  const companionMatch = line.match(/Companion:\s*(.+)/i);
  
  if (guideMatch || companionMatch) {
    const voice = guideMatch ? 'guide' : 'therapist';
    const text = (guideMatch || companionMatch)[1].trim();
    
    // ✅ AWAIT the preload - don't continue until it's done
    await preloadAudio(text, voice);
  }
};
  /**
   * PLAY AUDIO from cache or generate live
   */
const playAudio = async (text, voice) => {
  if (!isMicActive && pausedStateRef.current.isPaused) {
    log('⏸️ Paused - not starting audio', 'pause');
    return;
  }

  if (abortControllerRef.current?.signal.aborted) {
    log('Playback aborted', 'warn');
    return;
  }

  setTranscript(prev => [...prev, { 
    voice, 
    text, 
    timestamp: Date.now() 
  }]);

  if (isMicActive) {
    return speakChunk(text, voice);
  }

  const cacheKey = `${voice}-${text}`;
  const cached = audioQueueRef.current.get(cacheKey);

  if (cached && !cached.used) {
    log(`⚡ Playing from cache: ${voice}`, 'play');
    cached.used = true;

    return new Promise((resolve) => {
      const audio = new Audio(cached.url);
      audio.volume = isMutedRef.current ? 0 : 1;
      audio.playbackRate = 0.98;

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }

      let audioCompleted = false;

      const cleanup = () => {
        if (!audioCompleted) {
          audioCompleted = true;
          currentAudioRef.current = null;
          audio.pause();
          URL.revokeObjectURL(cached.url);
          audioQueueRef.current.delete(cacheKey);
          
          audio.removeEventListener('ended', cleanup);
          audio.removeEventListener('error', cleanup);
          
          log(`✅ Cached audio finished (${Math.round(audio.duration)}s)`, 'complete');
          resolve();
        }
      };

      const abortHandler = () => {
        log('Audio aborted via signal', 'warn');
        cleanup();
      };

      abortControllerRef.current?.signal.addEventListener('abort', abortHandler);

      audio.addEventListener('ended', cleanup);
      audio.addEventListener('error', cleanup);
      
      audio.addEventListener('canplay', () => {
        log(`📊 Cached audio ready: ${Math.round(audio.duration)}s at ${audio.playbackRate}x`, 'audio');
      });

      audio.play()
        .then(() => {
          log(`▶️ Cached audio playing: ${voice}`, 'play');
        })
        .catch((err) => {
          log(`❌ Cached play failed: ${err.message}`, 'error');
          if (err.name === 'NotAllowedError' && audioContextRef.current) {
            audioContextRef.current.resume().then(() => {
              audio.play().catch((retryErr) => {
                log(`❌ Retry failed: ${retryErr.message}`, 'error');
                cleanup();
              });
            });
          } else {
            cleanup();
          }
        });
    });
  }

  // Generate live if not cached
  log(`⚠️ Generating live: ${voice}`, 'warn');

  if (abortControllerRef.current?.signal.aborted) return;
  if (!isMicActive && pausedStateRef.current.isPaused) return;

  try {
    // ✅ USE SECURE API - NO MORE EXPOSED KEY
    const audioBlob = await SecureAPI.callHumeTTSSecure(
      text,
      VOICE_CONFIG[voice],
      0.98
    );

    const audioUrl = URL.createObjectURL(audioBlob);

    return new Promise((resolve) => {
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      audio.volume = isMutedRef.current ? 0 : 1;
      audio.playbackRate = 0.98;

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }

      let audioCompleted = false;

      const cleanup = () => {
        if (!audioCompleted) {
          audioCompleted = true;
          currentAudioRef.current = null;
          audio.pause();
          URL.revokeObjectURL(audioUrl);
          
          audio.removeEventListener('ended', cleanup);
          audio.removeEventListener('error', cleanup);
          
          log(`✅ Live audio finished (${Math.round(audio.duration)}s)`, 'complete');
          resolve();
        }
      };

      abortControllerRef.current?.signal.addEventListener('abort', cleanup);

      audio.addEventListener('ended', cleanup);
      audio.addEventListener('error', cleanup);
      
      audio.addEventListener('canplay', () => {
        log(`📊 Live audio ready: ${Math.round(audio.duration)}s at ${audio.playbackRate}x`, 'audio');
      });

      audio.play()
        .then(() => {
          log(`▶️ Live audio playing: ${voice}`, 'play');
        })
        .catch((err) => {
          log(`❌ Live play failed: ${err.message}`, 'error');
          cleanup();
        });
    });
  } catch (error) {
    log(`❌ Live audio error: ${error.message}`, 'error');
  }
};

// ============================================
// NEW: Fade out ambient smoothly
// ============================================

const fadeOutAmbient = () => {
  if (!ambientAudioRef.current) return;
  
  log('🎵 Fading out ambient...', 'ambient');
  
  const fadeInterval = setInterval(() => {
    if (ambientAudioRef.current && ambientAudioRef.current.volume > 0.05) {
      ambientAudioRef.current.volume -= 0.05;
    } else {
      clearInterval(fadeInterval);
      if (ambientAudioRef.current) {
        ambientAudioRef.current.pause();
        ambientAudioRef.current.currentTime = 0;
        log('✅ Ambient faded and stopped', 'ambient');
      }
    }
  }, 150); // Fade over ~1 second
};

// ============================================
// FALLBACK: Simple wrap-up if LLM fails
// ============================================

const playFallbackWrapUp = async () => {
  log('⚠️ Using fallback wrap-up', 'warn');
  
  const fallbacks = {
    morning: `Slowly, at your own pace, bring yourself back. You've spent this morning honoring yourself. Carry that with you as your day unfolds.`,
    afternoon: `Gently return now. You've reset and centered yourself. Take this calm forward into your afternoon.`,
    evening: `Let yourself come back slowly. You've released what needed releasing. Rest well tonight.`
  };
  
  const fallbackMsg = fallbacks[timeOfDay] || fallbacks.evening;
  
  isHostSpeakingRef.current = true;
  await speakChunk(fallbackMsg, 'guide');
  isHostSpeakingRef.current = false;
};


const pauseReflection = async () => {
  log('⏸️ Pausing reflection...', 'pause');
  
  // ✅ Set paused state FIRST
  pausedStateRef.current.isPaused = true;
  setIsPaused(true);
  setIsPlaying(false);

  // ✅ Stop current audio IMMEDIATELY
  if (currentAudioRef.current) {
    currentAudioRef.current.pause();
    currentAudioRef.current.currentTime = 0;
    currentAudioRef.current = null;
  }

  // ✅ Abort ALL ongoing operations
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
  }

  // Pause ambient
  if (ambientAudioRef.current) {
    ambientAudioRef.current.pause();
  }

  // Clear timers
  if (reflectionTimerRef.current) {
    clearTimeout(reflectionTimerRef.current);
    reflectionTimerRef.current = null;
  }

  await saveSessionState();
  
  log(`✅ Paused at ${pausedStateRef.current.currentIndex}/${pausedStateRef.current.scriptLines.length}`, 'pause');
};
const resumeReflection = async () => {
  log('▶️ Resuming reflection...', 'resume');

  pausedStateRef.current.isPaused = false;
  setIsPaused(false);
  setIsPlaying(true);
  // ✅ DON'T clear transcript - user might want to see conversation history


  // ✅ Ambient should already be playing, just continue
  if (ambientAudioRef.current && ambientAudioRef.current.paused) {
    try {
      await ambientAudioRef.current.play();
      log(`✅ Ambient resumed`, 'ambient');
    } catch (err) {
      log(`Ambient resume failed: ${err.message}`, 'warn');
    }
  }

  const { scriptLines, currentIndex } = pausedStateRef.current;

  // Generate brief transition
  if (scriptLines.length > 0 && currentIndex > 0) {
    const lastLine = scriptLines[currentIndex - 1] || scriptLines[currentIndex];
    const topicMatch = lastLine.match(/(?:Guide|Companion):\s*(.+)/i);
    const lastTopic = topicMatch ? topicMatch[1].trim().substring(0, 50) : 'your reflection';

    try {
      // ✅ Brief host transition instead of full welcome back
      const transitionVariations = [
        `So, where we were saying about ${lastTopic}, let's continue.`,
        `Right, back to what we were exploring with ${lastTopic}.`,
        `Let's pick back up where we left off.`,
        `Continuing on with that...`,
        `Let's keep going from here.`
      ];
      
      const transition = transitionVariations[Math.floor(Math.random() * transitionVariations.length)];

      isHostSpeakingRef.current = true;
      await speakChunk(transition, 'guide');
      isHostSpeakingRef.current = false;

      await new Promise(resolve => setTimeout(resolve, 500));

    } catch (err) {
      log(`⚠️ Transition error: ${err.message}`, 'warn');
    }
  }

  // ✅ Continue with script from saved position
  if (scriptLines.length > 0 && currentIndex < scriptLines.length) {
    const scriptText = scriptLines.join('\n');
    await playScript(scriptText, currentIndex);
  }
};

  /**
   * GENERATE LLM content
   */

const generateContent = async (context, systemPrompt) => {
  log('Generating wrap-up with LLM...', 'llm');
  
  try {
    const data = await SecureAPI.callAzureOpenAI([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: context }
    ], {
      temperature: 0.85,
      max_tokens: 200
    });

    const content = data.choices[0].message.content.trim();
    
    log('✅ LLM wrap-up generated', 'llm');
    return content;
    
  } catch (error) {
    log(`❌ LLM Error: ${error.message}`, 'error');
    throw error;
  }
};

// ============================================
// HELPER: Chunk Text for Natural Speech
// ============================================

const chunkTextForSpeech = (text, maxLength = 200) => {
  if (text.length <= maxLength) return [text];
  
  const chunks = [];
  const sentences = text.split(/(?<=[.!?])\s+/);
  
  let currentChunk = '';
  
  for (const sentence of sentences) {
    if (currentChunk.length + sentence.length <= maxLength) {
      currentChunk += (currentChunk ? ' ' : '') + sentence;
    } else {
      if (currentChunk) chunks.push(currentChunk);
      currentChunk = sentence;
    }
  }
  
  if (currentChunk) chunks.push(currentChunk);
  
  return chunks;
};



const stopReflection = () => {
  log('🛑 Stopping reflection...', 'stop');
  
  setHasStartedPlaying(false);

  // Abort any ongoing operations
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }

  // Stop current audio
  if (currentAudioRef.current) {
    currentAudioRef.current.pause();
    currentAudioRef.current.currentTime = 0;
    currentAudioRef.current = null;
  }

  // Stop ambient
  if (ambientAudioRef.current) {
    ambientAudioRef.current.pause();
    ambientAudioRef.current.currentTime = 0;
  }

  // Stop recognition
  safeStopRecognition(azureRecognizerRef);

  // Clear timers
  if (reflectionTimerRef.current) {
    clearTimeout(reflectionTimerRef.current);
  }
  if (inactivityTimerRef.current) {
    clearTimeout(inactivityTimerRef.current);
  }
  if (silenceTimerRef.current) {
    clearTimeout(silenceTimerRef.current);
  }

  // Clear audio cache
  audioQueueRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
  audioQueueRef.current.clear();

  // Reset all state
  pausedStateRef.current = { scriptLines: [], currentIndex: 0, isPaused: false };
  conversationHistoryRef.current = [];
  accumulatedTranscriptRef.current = '';
  
  setIsPlaying(false);
  setIsPaused(false);
  setIsInitializing(false);
  setIsMicActive(false);
  setIsListening(false);
  setMicStatus('');
  setUserTranscript('');
  setReflectionEndTime(null);
  setTranscript([]);
  setLoadingMessage('');
  setCurrentStatus('');
  setIsSuggestionMode(false);
  setIsShowingFarewell(false);
  
  isHostSpeakingRef.current = false;
  isMicActiveRef.current = false;
};

// ============================================
// ULTRA-NATURAL BREATHING GUIDANCE
// ============================================


// ============================================
// HELPER: Detect Breathing Cues
// ============================================

const detectNaturalBreathCue = (text) => {
  const patterns = {
    inhale: /breath(?:e)?\s+in|inhale|breathe\s+(?:with|in)/i,
    exhale: /breath(?:e)?\s+out|exhale|let\s+it\s+(?:go|out)|release/i,
    pause: /hold|stay\s+here|rest\s+here/i,
    general: /take\s+a\s+breath|feel\s+your\s+breath|notice.*breath|breathe/i
  };
  
  for (let [type, pattern] of Object.entries(patterns)) {
    if (pattern.test(text)) return type;
  }
  return null;
};

const detectMultiBreath = (text) => {
  const match = text.match(/(?:let's\s+)?take\s+(\d+|couple|few|three|two|several)\s+(?:deep\s+)?breaths?/i);
  if (!match) return 0;
  
  const num = match[1].toLowerCase();
  const numbers = {
    'two': 2,
    'couple': 2,
    'three': 3,
    'few': 3,
    'several': 4
  };
  
  return numbers[num] || parseInt(num) || 0;
};
// ✅ VARIATION PHRASES FOR NATURAL SOUNDING MEDITATION
const BREATHING_PHRASES = {
  intro: [
    "Let's breathe together.",
    "Take a moment to breathe with me.",
    "Let's synchronize our breath.",
    "Feel the rhythm of your breath.",
    "Let's settle into our breathing.",
    "Come back to your breath."
  ],
  
  inhale: [
    "Breathing in",
    "Inhale",
    "Take a breath in",
    "Breathe with me",
    "In through your nose",
    "Gently inhaling",
    "Breathing deeply in",
    "And in"
  ],
  
  exhale: [
    "And out",
    "Exhale",
    "Let it go",
    "Releasing",
    "Breathing out",
    "Letting your breath flow out",
    "Out through your mouth",
    "And exhaling"
  ],
  
  between: [
    "Hold it here",
    "Just breathing",
    "Notice the pause",
    "Feel that",
    "Stay with it",
    "And hold",
    "Let it settle"
  ],
  
  closing: [
    "Perfect.",
    "That's beautiful.",
    "Well done.",
    "Feel that calm.",
    "Notice the peace.",
    "You're grounded now."
  ]
};

const getRandomPhrase = (category) => {
  const phrases = BREATHING_PHRASES[category];
  return phrases[Math.floor(Math.random() * phrases.length)];
};
// 2. NATURAL BREATH FLOW - just like a real teacher

const guideBreathing = async (text, voice = 'guide') => {
  const breathType = detectNaturalBreathCue(text);
  
  const hour = new Date().getHours();
  let inhaleDuration = 4000;
  let exhaleDuration = 5000;
  
  if (hour >= 22 || hour < 6) {
    inhaleDuration = 5000;
    exhaleDuration = 6000;
  } else if (hour >= 6 && hour < 9) {
    inhaleDuration = 4000;
    exhaleDuration = 5000;
  } else if (hour >= 12 && hour < 18) {
    inhaleDuration = 4500;
    exhaleDuration = 5500;
  }
  
  log(`🫁 Guiding breathing: ${breathType}`, 'breath');
  
  const cycles = 3;
  
  for (let cycle = 0; cycle < cycles; cycle++) {
    // ✅ VARIED INHALE PHRASE
    let inhaleText;
    if (cycle === 0) {
      inhaleText = `${text} ${getRandomPhrase('inhale')}.`;
    } else {
      inhaleText = `${getRandomPhrase('inhale')}.`;
    }
    
    await speakChunk(inhaleText, voice);
    
    setTranscript(prev => [...prev, {
      voice: 'breath',
      text: '🫁 Breathing in · · ·',
      timestamp: Date.now(),
      isBreathCue: true
    }]);
    
    // ⏸️ LONGER PAUSE - User actually breathes
    await new Promise(resolve => setTimeout(resolve, inhaleDuration));
    
    // ✅ VARIED EXHALE PHRASE
    const exhaleText = `${getRandomPhrase('exhale')}.`;
    await speakChunk(exhaleText, voice);
    
    setTranscript(prev => [...prev, {
      voice: 'breath',
      text: '🫁 Breathing out · · ·',
      timestamp: Date.now(),
      isBreathCue: true
    }]);
    
    // ⏸️ LONGER PAUSE - User actually breathes
    await new Promise(resolve => setTimeout(resolve, exhaleDuration));
    
    // ✅ OPTIONAL BETWEEN PHRASE (not every cycle)
    if (cycle < cycles - 1 && Math.random() > 0.4) {
      const betweenPhrase = getRandomPhrase('between');
      await speakChunk(betweenPhrase, voice);
      await new Promise(resolve => setTimeout(resolve, 800));
    }
  }
  
  // ✅ VARIED CLOSING
  const closingPhrase = getRandomPhrase('closing');
  await speakChunk(closingPhrase, voice);
  
  log('✅ Breathing cycles complete', 'breath');
};

// ============================================
// MULTI-BREATH SEQUENCE (For "Take 3 breaths together")
// ============================================

const multiBreathSequence = async (count, voice = 'guide') => {
  const hour = new Date().getHours();
  const inhaleDuration = (hour >= 22 || hour < 6) ? 5000 : 4000;
  const exhaleDuration = inhaleDuration + 1000;
  
  log(`🫁 Starting ${count} breath cycles with variation`, 'breath');
  
  // ✅ VARIED INTRO
  const intro = getRandomPhrase('intro');
  await speakChunk(intro, voice);
  
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const cyclesToDo = count;
  
  for (let cycle = 0; cycle < cyclesToDo; cycle++) {
    // ✅ VARIED INHALE
    const inhaleText = getRandomPhrase('inhale');
    await speakChunk(inhaleText, voice);
    
    setTranscript(prev => [...prev, {
      voice: 'breath',
      text: '🫁 Breathing in · · ·',
      timestamp: Date.now(),
      isBreathCue: true
    }]);
    
    await new Promise(resolve => setTimeout(resolve, inhaleDuration));
    
    // ✅ VARIED EXHALE
    const exhaleText = getRandomPhrase('exhale');
    await speakChunk(exhaleText, voice);
    
    setTranscript(prev => [...prev, {
      voice: 'breath',
      text: '🫁 Breathing out · · ·',
      timestamp: Date.now(),
      isBreathCue: true
    }]);
    
    await new Promise(resolve => setTimeout(resolve, exhaleDuration));
    
    // ✅ OPTIONAL BETWEEN PHRASE
    if (cycle < cyclesToDo - 1 && Math.random() > 0.5) {
      const between = getRandomPhrase('between');
      await speakChunk(between, voice);
      await new Promise(resolve => setTimeout(resolve, 600));
    }
  }
  
  // ✅ VARIED CLOSING
  const closing = getRandomPhrase('closing');
  await speakChunk(closing, voice);
  
  log('✅ All breath cycles complete with natural variation', 'breath');
};

// 3. CREATE VISUAL BREATH GUIDE (dots appear in real-time)
const createBreathVisual = async (duration) => {
  const dotCount = Math.floor(duration / 1000);
  
  for (let i = 0; i < dotCount; i++) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setTranscript(prev => {
      const newTranscript = [...prev];
      const lastItem = newTranscript[newTranscript.length - 1];
      if (lastItem?.isBreathCue) {
        lastItem.text += ' ·';
      }
      return newTranscript;
    });
  }
};

// ============================================
// ENHANCED parseAndSpeakHosts - NATURAL BREATHING
// ============================================
const parseAndSpeakHosts = async (text, previousLine = null, nextLine = null) => {
  const guideMatch = text.match(/Guide:\s*(.+?)(?=Companion:|$)/s);
  const companionMatch = text.match(/Companion:\s*(.+?)$/s);

  const guideText = guideMatch ? guideMatch[1].trim() : null;
  const companionText = companionMatch ? companionMatch[1].trim() : null;
  
  if (pausedStateRef.current.isPaused) {
    log('⏸️ Paused - stopping parseAndSpeakHosts', 'pause');
    return;
  }
  
  // ✅ REMOVED: All breath detection logic
  // Just speak naturally without checking for breath cues
  
  if (guideText && companionText) {
    void preloadAudio(companionText, 'therapist');
    await speakChunk(guideText, 'guide');
    
    let pauseDuration = calculateNaturalPause(guideText, companionText);
    await new Promise(resolve => setTimeout(resolve, pauseDuration));
    
    await speakChunk(companionText, 'therapist');
    
  } else if (guideText) {
    await speakChunk(guideText, 'guide');
  } else if (companionText) {
    await speakChunk(companionText, 'therapist');
  }
};

const calculateNaturalPause = (previousText, nextText) => {
  let basePause = 400;
  
  // After questions - reflection time
  if (previousText?.match(/\?$/)) {
    basePause = 1100;
  }
  
  // After ellipsis - dramatic pause
  if (previousText?.match(/\.\.\.$/)) {
    basePause = 1400;
  }
  
  // After long sentences
  if (previousText?.match(/\.$/)) {
    const words = previousText.split(' ').length;
    if (words > 25) basePause += 500;
    if (words > 35) basePause += 400;
  }
  
  // Continuing thought (comma, "and", "so")
  if (previousText?.match(/,\s*$/) || previousText?.match(/\b(and|but|so|then)\s*$/i)) {
    basePause = 200;
  }
  
  // Body awareness transition
  if (previousText?.match(/notice|feel|sense|aware/i) && nextText?.match(/notice|feel|sense|aware/i)) {
    basePause = 1400;
  }
  
  // Natural human variation (±200ms)
  const variation = Math.random() * 400 - 200;
  
  return Math.max(150, Math.round(basePause + variation));
};

const handleIntentionSelect = (intention) => {
  setSelectedIntention(intention);
};

const handleIntentionSkip = async () => {
  setShowIntentionModal(false);
  setSelectedIntention({ id: 'general', label: 'General Meditation' });
  
  // Check for saved session or start fresh
  const savedSession = await loadSessionState();
  
  if (savedSession && savedSession.scriptLines.length > 0) {
    // ... existing restore logic
  } else {
    await runReflection();
  }
};

const handleIntentionContinue = async () => {
  if (!selectedIntention) return;
  
  setShowAllSet(true);
  
  // Wait 1.5 seconds for animation
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  setShowAllSet(false);
  setShowIntentionModal(false);
  
  await runReflection();
};
  const toggleMute = () => {
    const newMuted = !isMuted;
    log(`Mute toggled: ${newMuted}`, 'action');
    setIsMuted(newMuted);
  };

  const changeSpeed = () => {
    const speeds = [1.0, 1.15, 1.25, 1.5, 2.0];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    log(`Speed changed to: ${nextSpeed}x`, 'action');
    setPlaybackSpeed(nextSpeed);
  };


return (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    animation: 'fadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
  }}>
    <div style={{
      width: '100%',
      height: '92vh',
      borderTop: '2px solid #8d8d8dff',
      backgroundColor: '#1a1a1a',
      borderRadius: '28px 28px 0 0',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Blurred background layer */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'blur(150px) saturate(1.2)',
        transform: 'scale(1.1)',
        zIndex: 0
      }} />
      
      {/* Dark overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3))',
        zIndex: 1
      }} />

      {/* Header */}
{!isShowingFarewell && !showIntentionModal && (
  <div style={{ 
          padding: '24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
              </svg>
            </div>
            <div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#fff',
                marginBottom: '2px',
                textAlign: 'left'
              }}>
                Your Safe Space
              </div>
              <div style={{
                fontSize: '12px',
                display: 'block',
                textAlign: 'left',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                {currentStatus || `${timeOfDay.charAt(0).toUpperCase() + timeOfDay.slice(1)} meditation session`}
              </div>
            </div>
          </div>
          
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      )}

      {/* Content Area - Transcript */}
{!isShowingFarewell && (
  <div 
    ref={transcriptRef} 
    style={{ 
      flex: 1,
      position: 'relative',
      zIndex: 2,
      overflowY: 'auto',
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      paddingBottom: interactionMode === 'text' ? '180px' : '120px'  // ✅ ADD SPACE FOR CONTROLS
    }}
  >
          {/* Initial "Tap Play" Message */}
          {!hasStartedPlaying && !isPlaying && !interactionMode && (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'fadeInUp 0.6s ease'
            }}>
              <div style={{ textAlign: 'center', animation: 'fadeInUp 0.6s ease 0.2s backwards' }}>        
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#ffffff',
                  marginBottom: '8px',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                }}>
                  Tap the play button below to begin!
                </h3>
                <p style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: '1.5'
                }}>
                  Your personalized audio reflection awaits
                </p>
              </div>
            </div>
          )}

          {/* Transcript content */}
          {hasStartedPlaying && (transcript.length > 0 || loadingMessage || (interactionMode && micStatus)) && (
            <div style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column' }}>
              {/* Loading Message */}
              {loadingMessage && !interactionMode && (
                <div style={{ 
                  marginBottom: '20px',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#ffffff',
                    marginTop: '8px',
                    flexShrink: 0,
                    animation: 'pulseGlow 1.5s infinite'
                  }} />
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '500',
                    lineHeight: '1.6',
                    flex: 1,
                    color: '#ffffff'
                  }}>
                    {loadingMessage}
                  </div>
                </div>
              )}

              {/* Transcript lines */}
{transcript.slice(-5).map((item, idx, arr) => {
  const isLatest = idx === arr.length - 1;
  const isUser = item.voice === 'user';
  const isBreath = item.voice === 'breath';
  const isSystem = item.voice === 'system';
  const isGuide = item.voice === 'guide';
  const isTherapist = item.voice === 'therapist';

  // ✅ DETERMINE COLORS
  let dotColor = 'rgba(255, 255, 255, 0.3)';
  let textColor = 'rgba(255, 255, 255, 0.5)';
  let label = '';

  if (isUser) {
    // USER: GREEN
    dotColor = '#10b981';
    textColor = '#10b981';
    label = 'You: ';
  } else if (isBreath) {
    // BREATH: GREEN
    dotColor = '#10b981';
    textColor = '#10b981';
    label = '🫁 ';
  } else if (isSystem) {
    // ERROR: RED
    dotColor = '#ef4444';
    textColor = '#ef4444';
    label = '⚠️ ';
  } else if (isGuide || isTherapist) {
    // HOST: WHITE (brighter if latest)
    dotColor = isLatest ? '#ffffff' : '#a0aec0';
    textColor = isLatest ? '#ffffff' : '#cbd5e1';
    label = ''; // ✅ No label, just the dot
  }

  return (
    <div
      key={`${item.timestamp}-${idx}`}
      style={{
        marginBottom: '24px',
        textAlign: 'left',
        animation: 'fadeInUp 0.4s ease-out',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        opacity: isLatest ? 1 : 0.8,
        transition: 'opacity 0.3s ease'
      }}
    >
      {/* Dot Indicator */}
      <div
        style={{
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          background: dotColor,
          marginTop: '6px',
          flexShrink: 0,
          transition: 'all 0.3s ease',
          boxShadow: isLatest ? `0 0 8px ${dotColor}` : 'none'
        }}
      />

      {/* Text Content */}
      <div
        style={{
          fontSize: '20px',
          fontWeight: isLatest ? '600' : '500',
          color: textColor,
          lineHeight: '1.6',
          wordWrap: 'break-word',
          flex: 1,
          transition: 'all 0.3s ease'
        }}
      >
        {label && (
          <span style={{ fontWeight: '700', marginRight: '4px' }}>
            {label}
          </span>
        )}
        {item.text}
      </div>
    </div>
  );
})}

            </div>
          )}

          {/* Suggestions Section */}
          {hasStartedPlaying && !interactionMode && !isSuggestionMode && !isShowingFarewell && (
            <div style={{ marginTop: 'auto' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '16px',
                textAlign: 'left'
              }}>
                Suggestions
              </h3>
              
              <div style={{
                display: 'flex',
                gap: '12px',
                overflowX: 'auto',
                paddingBottom: '12px',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}>
                {isLoadingSuggestions ? (
                  [...Array(5)].map((_, idx) => (
                    <div key={`skeleton-${idx}`} style={{
                      minWidth: '140px',
                      height: '180px',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.08)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                        animation: 'shimmer 2s infinite'
                      }} />
                    </div>
                  ))
                ) : (
                  suggestionTopics.map((topic, idx) => (
                    <button key={idx} onClick={() => handleSuggestionClick(topic)} style={{
                      minWidth: '140px',
                      height: '180px',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <img src={topic.image} alt={topic.title} style={{
                        width: '100%',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: '12px 12px 0 0'
                      }} />
                      <div style={{ padding: '12px', textAlign: 'left' }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#ffffff',
                          marginBottom: '4px'
                        }}>
                          {topic.title}
                        </div>
                        <div style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.6)' }}>
                          {topic.duration}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================
          BOTTOM SECTION: Audio controls on top, text input below
          ============================================ */}
      {!isShowingFarewell && (
        <div style={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(244, 251, 255, 0.15)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.56)',
          borderRadius: '42px 42px 0 0'
        }}>
          
          {/* AUDIO CONTROLS - ON TOP */}
          <div style={{
            padding: '16px 20px',
            borderBottom: interactionMode === 'text' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
          }}>
            <div style={{
              maxWidth: '600px',
              margin: '0 auto',
              background: '#ffffff',
              borderRadius: '32px',
              padding: '8px 20px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(169, 177, 185, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              {/* Join/Leave Button */}
              {!interactionMode ? (
                <button 
                  onClick={handleJoinClick}
                  disabled={isInitializing || !isPlaying}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '20px',
                    background: (isInitializing || !isPlaying) ? '#cccccc' : '#000000',
                    border: 'none',
                    cursor: (isInitializing || !isPlaying) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    opacity: (isInitializing || !isPlaying) ? 0.6 : 1,
                    flexShrink: 0
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9v3a5.006 5.006 0 0 1-5 5h-4a5.006 5.006 0 0 1-5-5V9m7 9v3m-3 0h6M11 3h2a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3Z"/>
                  </svg>  
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'white' }}>
                    Join In
                  </span>
                </button>
              ) : (
                <button 
                  onClick={leaveConversation}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '20px',
                    background: interactionMode === 'voice' ? '#10b981' : '#000000',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    animation: interactionMode === 'voice' ? 'pulse 1.5s infinite' : 'none',
                    flexShrink: 0
                  }}
                >
                  {interactionMode === 'voice' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9v3a5.006 5.006 0 0 1-5 5h-4a5.006 5.006 0 0 1-5-5V9m7 9v3m-3 0h6M11 3h2a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3Z"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                      <path fillRule="evenodd" d="M3 5.983C3 4.888 3.895 4 5 4h14c1.105 0 2 .888 2 1.983v8.923a1.992 1.992 0 0 1-2 1.983h-6.6l-2.867 2.7c-.955.899-2.533.228-2.533-1.08v-1.62H5c-1.105 0-2-.888-2-1.983V5.983Zm5.706 3.809a1 1 0 1 0-1.412 1.417 1 1 0 1 0 1.412-1.417Zm2.585.002a1 1 0 1 1 .003 1.414 1 1 0 0 1-.003-1.414Zm5.415-.002a1 1 0 1 0-1.412 1.417 1 1 0 1 0 1.412-1.417Z" clipRule="evenodd"/>
                    </svg>
                  )}
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'white' }}>
                    Leave
                  </span>
                </button>
              )}

              {/* Play/Pause Button */}
              <button 
                onClick={togglePlayPause}
                disabled={isInitializing}
                style={{
                  width: '58px',
                  height: '58px',
                  borderRadius: '50%',
                  background: isInitializing ? '#6b7280' : '#000',
                  border: 'none',
                  cursor: isInitializing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isInitializing ? 0.6 : 1,
                  flexShrink: 0
                }}
              >
                {isPlaying ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <rect x="6" y="5" width="4" height="14" rx="1.5"/>
                    <rect x="14" y="5" width="4" height="14" rx="1.5"/>
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
              
              {/* Speed Control */}
              <button
                onClick={changeSpeed}
                style={{
                  padding: '12px 16px',
                  borderRadius: '20px',
                  background: '#f3f4f6',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#000000',
                  fontSize: '14px',
                  fontWeight: 600,
                  flexShrink: 0
                }}
              >
                {playbackSpeed}x
              </button>
            </div>
          </div>

          {/* TEXT INPUT - AT BOTTOM (only shown when in text mode) */}
{interactionMode === 'text' && !isShowingFarewell && (
                <div style={{
              padding: '10px 20px 24px',
          background: 'rgba(150, 150, 150, 0.09)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
animation: 'none',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)'
                      }}>
              <div style={{
                maxWidth: '600px',
                margin: '0 auto',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '24px',
                padding: '6px 8px 8px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}>
                <input
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !isSendingText && handleTextSubmit()}
                  placeholder="Type your message..."
                  disabled={isSendingText}
                  autoFocus
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '15px',
                    color: '#000000',
                    fontWeight: '500',
                    fontFamily: 'inherit'
                  }}
                />
                <button
                  onClick={handleTextSubmit}
                  disabled={!textInput.trim() || isSendingText}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: textInput.trim() && !isSendingText ? '#000000' : '#e0e0e0',
                    border: 'none',
                    cursor: textInput.trim() && !isSendingText ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    flexShrink: 0
                  }}
                >
<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m5 15 7-7 7 7"/>
</svg>

                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Join Modal */}
      {showJoinModal && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 40,
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            padding: '32px',
            maxWidth: '400px',
            margin: '0 20px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '12px'
            }}>
              How would you like to join?
            </h3>
            
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              Choose your preferred way to interact
            </p>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <button
                onClick={() => selectInteractionMode('voice')}
                style={{
                  padding: '10px 28px',
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9v3a5.006 5.006 0 0 1-5 5h-4a5.006 5.006 0 0 1-5-5V9m7 9v3m-3 0h6M11 3h2a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3Z"/>
                </svg>   
                Voice
              </button>
              
              <button
                onClick={() => selectInteractionMode('text')}
                style={{
                  padding: '10px 28px',
                  borderRadius: '20px',
                  background: '#ffffff',
                  border: 'none',
                  color: '#000000',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#000000">
                  <path fillRule="evenodd" d="M3 5.983C3 4.888 3.895 4 5 4h14c1.105 0 2 .888 2 1.983v8.923a1.992 1.992 0 0 1-2 1.983h-6.6l-2.867 2.7c-.955.899-2.533.228-2.533-1.08v-1.62H5c-1.105 0-2-.888-2-1.983V5.983Zm5.706 3.809a1 1 0 1 0-1.412 1.417 1 1 0 1 0 1.412-1.417Zm2.585.002a1 1 0 1 1 .003 1.414 1 1 0 0 1-.003-1.414Zm5.415-.002a1 1 0 1 0-1.412 1.417 1 1 0 1 0 1.412-1.417Z" clipRule="evenodd"/>
                </svg>
                Text
              </button>
            </div>
            
            <button
              onClick={cancelJoinModal}
              style={{
                padding: '10px 20px',
                borderRadius: '16px',
                background: 'transparent',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Suggestion Confirmation Modal */}
      {showSuggestionConfirm && selectedSuggestion && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: '30px',
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 40,
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            padding: '32px',
            maxWidth: '400px',
            margin: '0 20px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '100%',
              height: '120px',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '20px'
            }}>
              <img 
                src={selectedSuggestion.image}
                alt={selectedSuggestion.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '12px'
            }}>
              {selectedSuggestion.title}
            </h3>
            
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              Would you like to explore this topic in a personalized reflection session?
            </p>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={cancelSuggestion}
                style={{
                  padding: '12px 24px',
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={confirmSuggestion}
                style={{
                  padding: '12px 24px',
                  borderRadius: '20px',
                  background: '#ffffff',
                  border: 'none',
                  color: '#000000',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Yes, Let's Begin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Farewell Screen */}
      {isShowingFarewell && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          animation: 'fadeIn 0.6s ease',
          padding: '40px 24px'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '24px',
            animation: 'fadeInDown 0.6s ease'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '6px',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              lineHeight: '1.3'
            }}>
              {timeOfDay === 'morning' ? 'Have a wonderful day' :
               timeOfDay === 'afternoon' ? 'Have a lovely afternoon' :
               'Have a peaceful evening'} ✨
            </h2>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.12)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            borderRadius: '28px',
            padding: '28px 24px',
            maxWidth: '600px',
            width: '100%',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            animation: 'scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
            position: 'relative',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              Continue Your Journey
            </h3>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              overflowX: 'auto',
              paddingBottom: '16px'
            }}>
              {suggestionTopics.map((topic, idx) => (
                <button key={idx} onClick={() => handleSuggestionClick(topic)} style={{
                  minWidth: '160px',
                  height: '200px',
                  borderRadius: '16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <img src={topic.image} alt={topic.title} style={{
                    width: '100%',
                    height: '130px',
                    objectFit: 'cover',
                    borderRadius: '16px 16px 0 0'
                  }} />
                  <div style={{ padding: '14px', textAlign: 'left' }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#ffffff',
                      marginBottom: '6px'
                    }}>
                      {topic.title}
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {topic.duration}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={closeFarewell}
            style={{
              padding: '14px 48px',
              borderRadius: '24px',
              background: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#000000',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
              animation: 'fadeInUp 0.6s ease 0.3s backwards'
            }}
          >
            Close
          </button>
        </div>
      )}
{/* Intention Modal */}
{showIntentionModal && (
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    animation: 'fadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards'
  }}>
    {/* Close Button */}
    <button
      onClick={onClose}
      style={{
        position: 'absolute',
        top: '24px',
        right: '24px',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.1)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 51,
        transition: 'all 0.2s',
        animation: 'fadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s backwards'
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>
    
    {showAllSet ? (
      // "All Set!" Animation
      <div style={{
        animation: 'fadeInScale 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
        textAlign: 'center'
      }}>
        <div style={{
          width: '100px',
          height: '100px',
          margin: '0 auto 20px',
          animation: 'scaleIn 0.3s ease'
        }}>
          <svg width="100" height="100" viewBox="0 0 100 100" fill="none">
            <circle cx="50" cy="50" r="40" stroke="white" strokeWidth="2" fill="none"
              style={{
                animation: 'drawCircle 0.5s ease-out forwards',
                strokeDasharray: 251,
                strokeDashoffset: 251
              }}
            />
            <path d="M 30 50 L 42 62 L 70 35" stroke="white" strokeWidth="3" fill="none"
              strokeLinecap="round" strokeLinejoin="round"
              style={{
                animation: 'drawCheck 0.4s ease-out 0.4s forwards',
                strokeDasharray: 80,
                strokeDashoffset: 80
              }}
            />
          </svg>
        </div>
        <p style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#fff',
          animation: 'fadeIn 0.3s ease 0.8s backwards'
        }}>
          All set!
        </p>
      </div>
    ) : (
      // Intention Selection Container
      <div style={{
        background: 'rgba(255, 255, 255, 0.12)',
        backdropFilter: 'blur(10px)',
        borderRadius: '48px',
        padding: '40px 32px',
        maxWidth: '480px',
        width: '90%',
        margin: '0 20px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        animation: 'fadeInScale 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
        position: 'relative',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Title */}
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '8px',
          textAlign: 'center',
          animation: 'fadeInDown 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.1s backwards'
        }}>
          What would you like to focus on?
        </h3>
        
        {/* Subtitle */}
        <p style={{
          fontSize: '13px',
          color: 'rgba(255, 255, 255, 0.7)',
          marginBottom: '24px',
          textAlign: 'center',
          lineHeight: '1.5',
          animation: 'fadeInDown 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.15s backwards'
        }}>
          Choose an intention for today's session
        </p>
        
        {/* Slide Indicator Dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
          marginBottom: '16px',
          animation: 'fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s backwards'
        }}>
          {[...Array(Math.ceil(intentionOptions.length / 4))].map((_, idx) => (
            <div
              key={idx}
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: intentionScrollIndex === idx 
                  ? 'rgba(255, 255, 255, 0.8)' 
                  : 'rgba(255, 255, 255, 0.3)',
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </div>
        
        {/* Options List */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          marginBottom: '24px',
          animation: 'fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.25s backwards'
        }}>
          {visibleIntentions.map((option, idx) => (
            <button
              key={option.id}
              onClick={() => handleIntentionSelect(option)}
              style={{
                padding: '13px 20px',
                borderRadius: '32px',
                background: selectedIntention?.id === option.id 
                  ? 'rgba(255, 255, 255, 0.25)' 
                  : 'rgba(255, 255, 255, 0.04)',
                border: `1px solid ${selectedIntention?.id === option.id ? '#ffffff80' : '#ffffff45'}`,
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                textAlign: 'left',
                animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) backwards',
                animationDelay: `${0.3 + idx * 0.08}s`,
                transform: 'translateY(0)',
                opacity: 1
              }}
            >
              <span style={{ 
                fontSize: '22px',
                flexShrink: 0
              }}>
                {option.icon}
              </span>
              <span style={{ 
                fontSize: '14px', 
                lineHeight: '1.4',
                flex: 1
              }}>
                {option.label}
              </span>
            </button>
          ))}
        </div>
        
        {/* Navigation Arrows */}
        {(hasPrev || hasMore) && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '20px',
            animation: 'fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.4s backwards'
          }}>
            {hasPrev && (
              <button
                onClick={() => handleIntentionScroll('prev')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '28px',
                  background: 'rgba(255, 255, 255, 1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#000000ff',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                Previous
              </button>
            )}
            
            {hasMore && (
              <button
                onClick={() => handleIntentionScroll('next')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '28px',
                  background: 'rgba(255, 255, 255, 1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#000000ff',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
              >
                More
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            )}
          </div>
        )}
        
        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.45s backwards'
        }}>
          <button
            onClick={handleIntentionSkip}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '32px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              ':hover': {
                background: 'rgba(255, 255, 255, 0.15)'
              }
            }}
          >
            Skip
          </button>
          
          <button
            onClick={handleIntentionContinue}
            disabled={!selectedIntention}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '32px',
              background: selectedIntention ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
              border: 'none',
              color: '#000000',
              fontSize: '14px',
              fontWeight: '600',
              cursor: selectedIntention ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              opacity: selectedIntention ? 1 : 0.5
            }}
          >
            Continue
          </button>
        </div>
      </div>
    )}
  </div>
)}

{/* CSS Animations */}
<style>{`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.92);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes drawCircle {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes drawCheck {
    to {
      stroke-dashoffset: 0;
    }
  }
`}</style>
      <style>{`
      @keyframes drawCircle {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes drawCheck {
  to {
    strokeDashoffset: 0;
  }
}
        @keyframes slideUpInput {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.9);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        @keyframes pulseGlow {
          0%, 100% { 
            opacity: 1;
            box-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
          }
          50% { 
            opacity: 0.6;
            box-shadow: 0 0 4px rgba(255, 255, 255, 0.3);
          }
        }

        @keyframes slideUpp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          /* Slightly reduce text input bottom spacing on mobile */
        }
            @keyframes drawCircle {
    to {
      stroke-dashoffset: 0;
    }
  }
  
  @keyframes drawCheck {
    to {
      stroke-dashoffset: 0;
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes scaleIn {
    from {
      transform: scale(0.8);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  @keyframes fadeInScale {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
      `}</style>
    </div>
  </div>
);
};
export default JournalReflectionAudio;